/**
 * Entry point: boot PixiJS, bake every texture, and run the loop.
 *
 * The loop is a fixed-step accumulator. The simulation only ever advances in
 * whole 30 Hz ticks; rendering interpolates between the last two. Hitstop works
 * by simply not advancing the accumulator, which is why a heavy blow freezes the
 * world without desynchronising anything.
 */

import '@fontsource/cinzel/400.css';
import '@fontsource/cinzel/600.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
/**
 * Before anything else of Pixi's: the eval-free implementations.
 *
 * Pixi's WebGL renderer builds its uniform uploaders with `new Function`, and
 * the packaged shell's CSP (`default-src 'self'`, no eval) refuses that — so
 * the exe booted to 'it did not light' on any machine whose WebView fell back
 * from WebGPU to WebGL, while every browser run looked fine. This module swaps
 * those code-generated paths for plain ones; the CSP stays strict, and the
 * browser build pays a negligible cost for taking the same road.
 */
import 'pixi.js/unsafe-eval';
import { Application } from 'pixi.js';

import { TICK_DT, MAP_H, MAP_W } from './sim/constants.ts';
import { createWorld, initRun, statsOf, type World } from './sim/world.ts';
import { legacyEarned } from './sim/bloodline.ts';
import { bloodline, ownedBloodline } from './ui/bloodline.ts';
import { unitType, VILLAGER_TYPE } from './data/units.ts';
import { stepGame, stepWorld } from './sim/step.ts';
import { hashWorld } from './sim/hash.ts';
import { Session, relayUrl } from './net/session.ts';
import { adoptRealm, applyRealmUpdate } from './net/realm.ts';
import { authSetup, goCanonical, SignIn } from './net/auth.ts';
import { AccountService } from './net/accounts.ts';
import { AccountBar, ensureAccount, signInFailed } from './ui/account.ts';
import { Cmd, cmd, CommandQueue, type Command } from './sim/commands.ts';
import { canPlaceBuilding, canPlaceTower, lineTiles, rubbleReadout, wallLineTiles } from './sim/apply.ts';
import { TOWER_SIZE, isCurtain, nearestCurtain } from './sim/systems/curtain.ts';
import { Formation, Stance } from './sim/world.ts';
import { Phase, Team, UF } from './sim/types.ts';
import { buildingType } from './data/buildings.ts';
import { power, powerOrNull } from './data/powers.ts';
import { resolvePower } from './sim/systems/powers.ts';
import { heroAbilitySlots, heroUnit, HERO_ABILITY_RANGE } from './sim/systems/hero.ts';
import { seedFromString, seedToCode } from './core/rng.ts';
import { clamp } from './core/math.ts';

import { TextureBank } from './render/textures.ts';
import { Scene, type BuildPreview } from './render/scene.ts';
import { ZOOM_TACTICAL } from './render/camera.ts';
import { Hud, type BuildSelection } from './ui/hud.ts';
import { TouchInput } from './ui/touch.ts';
import { reliefImage, seaImage, textureImage } from './ui/relief.ts';
import { DebugConsole } from './ui/console.ts';
import { Menu, type CampaignChoice } from './ui/menu.ts';
import { Lobby } from './ui/lobby.ts';
import { Tutorial } from './ui/tutorial.ts';
import { WorldMap } from './ui/worldmap.ts';
import { Travel } from './ui/travel.ts';
import { WarCall } from './ui/warcall.ts';
import { RealmEventModal } from './ui/realmevent.ts';
import { Manage } from './ui/manage.ts';
import { LevyBoard } from './ui/levies.ts';
import {
  armyStrength,
  carryCharacterInto,
  createBattle,
  takeCharacterHome,
  type BattleConfig,
} from './sim/battle.ts';
import { createCampaignWorld } from './sim/campaign/state.ts';
import { battleConfigFor, isHomeAssault, myBattleTeam } from './sim/campaign/conquest.ts';
import { findArmy } from './sim/campaign/armies.ts';
import { shelteredIn } from './sim/campaign/garrisons.ts';
import { WarnKind } from './sim/campaign/types.ts';
import { nationDef, nationAccent } from './data/nations.ts';
import { provinceDef } from './data/provinces.ts';
import { buildingTypeIndex } from './data/buildings.ts';
import { toCss } from './data/palette.ts';
// The guided run names no tier of its own: there is exactly one, and the entry
// used to pass the string 'ember', which `difficulty()` has silently resolved to
// this same tier ever since the five became one.
import { DIFFICULTIES } from './data/difficulty.ts';
import {
  assaultSummary,
  beginHomeAssault,
  homeAssaultResult,
  capitalMeasure,
  syncCapitalDevelopment,
  type HomeAssaultSnapshot,
} from './sim/campaign/homefront.ts';
import { createHoldWorld, holdBlock } from './sim/campaign/holdworld.ts';
import { Audio } from './audio/synth.ts';
import {
  flushSaves,
  initStorage,
  newestSlot,
  readGame,
  savesPending,
  storageStatus,
  writeGame,
  type LoadedGame,
} from './ui/storage.ts';
import { loadWorld, saveWorld } from './sim/save.ts';

// The bindings and the labels the HUD prints on the buttons come from the same
// list, in `ui/keys.ts`. They used to be two lists with a comment asking the
// next person to keep them in step, and they did not stay in step.
import {
  HERO_HOTKEYS,
  KEY_CONSOLE,
  KEY_FASTER,
  KEY_FORMATION,
  KEY_HOLD,
  KEY_HORN,
  KEY_LEDGER,
  KEY_LEVIES,
  KEY_LOAD,
  KEY_MUTE,
  KEY_PAUSE,
  KEY_RALLY,
  KEY_SAVE,
  KEY_SHEET,
  KEY_SHIELDWALL,
  KEY_SLOWER,
  KEY_STANCE,
  KEY_STATS,
  KEY_SWAP,
  POWER_HOTKEYS,
  keyLabel,
} from './ui/keys.ts';

async function boot(): Promise<void> {
  const status = document.getElementById('boot-status') as HTMLElement;
  const setStatus = (s: string) => {
    status.textContent = s;
  };

  const app = new Application();
  await app.init({
    background: 0x14120f,
    antialias: true,
    resolution: Math.min(2, window.devicePixelRatio || 1),
    autoDensity: true,
    resizeTo: window,
    preference: 'webgl',
  });
  (document.getElementById('app') as HTMLElement).appendChild(app.canvas);

  // Where saves live has to be settled before anything asks what is saved, and
  // the menu asks on its first frame. In a browser this returns immediately; on
  // the desktop it finds a writable folder and reads the slots off the disk.
  setStatus('finding the strongbox…');
  await initStorage();

  /**
   * Who is playing, settled before anything asks.
   *
   * Two things happen here and both are quick. The server is asked whether it
   * wants accounts at all — it is the one that knows, and it serves the issuer
   * and client id so nothing about the identity provider is baked into this
   * bundle. And if this page load is the way back from a sign-in, the code in
   * the address bar is spent now, while there is still a boot screen over the
   * top of it, rather than after the title has drawn.
   */
  const authCfg = await authSetup();
  // A hearth that signs people in does it on one address. If this is another
  // one, the page leaves now — before a verifier is written to an origin the
  // provider will not send anybody back to.
  if (goCanonical(authCfg)) return;
  const signIn = new SignIn(authCfg);
  const accounts = new AccountService(authCfg, signIn);
  try {
    if (await signIn.finish()) {
      void accounts.touch().catch(() => {});
    } else {
      /**
       * Not back from the provider — so this is somebody who was signed in here
       * before, and the browser still holds their tokens.
       *
       * The ID token has almost certainly expired: providers give them an hour
       * and people come back the next evening. `resume` spends the refresh
       * token on a new one before anything paints, which is what makes the
       * title screen say their name instead of "Sign in". Awaited rather than
       * left to run: the account bar reads `signedIn` synchronously as it
       * mounts, and a renewal that lands afterwards would draw the door first
       * and the name a moment later, which looks exactly like being logged out.
       */
      if (await signIn.resume()) void accounts.touch().catch(() => {});
    }
  } catch (e) {
    signInFailed((e as Error).message);
  }
  /**
   * The name this browser plays as, once it is known.
   *
   * Cached because it is a request over the network and the answer does not
   * change while somebody sits at the title screen — except by their own hand,
   * which is why the account bar reports back into it. Signing out sets it to
   * null and the door closes again.
   *
   * Declared before the bar that writes to it: with nobody signed in the bar
   * answers *synchronously*, and a `let` below this point would be reached
   * before it exists.
   */
  let myHandle: string | null = null;

  /**
   * True once the title screen is drawn.
   *
   * Declared here, above the bar that reads it, for the same reason `myHandle`
   * is: with nobody signed in the bar answers *synchronously* during `mount`,
   * and a `let` below this point would be read before it exists — which is a
   * crash on boot for every signed-out player, i.e. everybody's first visit.
   */
  let running = false;

  /**
   * Who you are, on the title screen, where you can do something about it.
   *
   * Not awaited: it asks the accounts module for a handle, and a slow answer to
   * "what am I called" must not hold up the game starting. It appears when it
   * knows, which on any working hearth is before the boot screen has faded.
   */
  const accountBar = new AccountBar(signIn, accounts, (handle) => {
    myHandle = handle;
    /**
     * Signing out puts the door back, rather than leaving somebody standing at
     * a title screen they are no longer allowed to use.
     *
     * Only once the game is up: this same callback fires during boot, before
     * there is a menu behind the card, and the door is raised there by the line
     * at the end of `boot` instead.
     */
    if (!handle && running && signIn.configured) void requireAccount();
  });
  void accountBar.mount();

  /**
   * No run starts without an account, on a hearth that asks for one.
   *
   * Returns the handle, or null if the player is not going to play — they backed
   * out of the card, or the tab is about to leave for the identity provider and
   * will come back a fresh page.
   *
   * A hearth that does not ask returns null too, and callers read that as "no
   * gate here" rather than as a refusal: the check is always `signIn.configured`
   * first, so the two nulls never get confused.
   */
  let asking: Promise<string | null> | null = null;
  async function requireAccount(): Promise<string | null> {
    if (!signIn.configured) return null;
    if (myHandle) return myHandle;
    /**
     * One question at a time.
     *
     * The door goes up by itself when the title screen appears, and the play
     * buttons ask again — including by keyboard, which passes over an overlay
     * that only blocks the mouse. Without this, pressing Enter behind the card
     * would raise a second one on top of the first, and the first would sit
     * waiting on a card that no longer exists.
     */
    asking ??= ensureAccount(signIn, accounts).finally(() => {
      asking = null;
    });
    myHandle = await asking;
    // The corner shows who you are, and this is the other place you can become
    // somebody: a name taken at the door has to reach it, or it goes on saying
    // "take a name" to somebody who just did.
    accountBar.told(myHandle);
    return myHandle;
  }

  /**
   * Put a door in front of something that starts a run.
   *
   * Wrapping the callbacks rather than teaching the menu about accounts, for two
   * reasons. The menu is a list of things to play and has no business knowing
   * what an OIDC subject is; and every way into a run comes through one of these
   * six functions, so a door here cannot be walked around by a button somebody
   * adds next week — a new mode either goes through this and is gated, or does
   * not start a run at all.
   */
  function behindAccount<A extends unknown[]>(play: (...args: A) => void): (...args: A) => void {
    return (...args: A) => {
      if (!signIn.configured) {
        play(...args);
        return;
      }
      void requireAccount().then((handle) => {
        if (handle) play(...args);
      });
    };
  }

  setStatus('cutting stone…');
  await nextFrame();
  const bank = new TextureBank(app.renderer);
  bank.build();

  setStatus('mustering…');
  await nextFrame();

  const scene = new Scene(app, bank);
  scene.resize(app.screen.width, app.screen.height);

  // ── World ──────────────────────────────────────────────────────────────
  const params = new URLSearchParams(location.search);
  const seed = params.get('seed') ? seedFromString(params.get('seed') as string) : (Date.now() & 0x7fffffff);
  let world: World = createWorld({
    seed,
    factionId: params.get('faction') ?? 'ashen',
    difficultyId: params.get('difficulty') ?? 'endless',
    sandbox: false,
    bloodline: ownedBloodline(),
  });
  initRun(world);

  const queue = new CommandQueue();
  scene.camera.centreOn(world.map.hearthX, world.map.hearthY);
  scene.camera.setZoom(34, true);

  /** Swap the whole world out — used by the menu and by "again". */
  function adoptWorld(next: World, centreX: number, centreY: number, zoom: number): void {
    /**
     * Your colours are the player's until a shared field says otherwise.
     *
     * `myTeam` decides what a click can select, and it was set when entering
     * a battle between two crowns and never put back — so a player who
     * defended one went on commanding the enemy's colours afterwards, which
     * looks exactly like the mouse having stopped working. It resets on every
     * world we adopt; `enterCampaignBattle` sets it again, after this.
     */
    myTeam = Team.Player;
    world = next;
    scene.clear();
    scene.camera.centreOn(centreX, centreY);
    scene.camera.setZoom(zoom, true);
    selected = [];
    hud.setSelection(selected);
    hud.hideModal();
    hud.clearBuildSelection();
    hud.clearArmedPower();
    // A slot index means nothing in a world it was not taken from.
    selectedBuilding = -1;
    hud.setBuildingSelection(-1);
    manage.hide();
    // The town list names worlds, and every one of its rows refers to a place
    // we may have just left. It closes with the world it was opened over.
    travel.closeList();
    realmQueue.drain();
    buildSel = { kind: 'none' };
    lastRelicOffer = 0;
    shownEventKey = '';
    lastAutosaveWave = -1;
    lastAutosaveTurn = -1;
    // A different realm may be standing on the same turn number as the last one.
    lastWarnTurn = -1;
    capitalWarnKey = '';
    battleOutcomeShown = false;
    // A world swap mid-drag would otherwise leave the drag live and apply it to
    // the new world's companies on the next mouseup.
    dragStart = null;
    boxSelecting = false;
    rightDrag = null;
    wallDragStart = null;
    queue.drain();
  }

  // ── UI state ───────────────────────────────────────────────────────────
  let buildSel: BuildSelection = { kind: 'none' };
  let selected: number[] = [];
  /** The building slot whose panel is open, or −1. Mirrors the HUD's own copy. */
  let selectedBuilding = -1;
  const hotkeyGroups = new Map<number, number[]>();

  /**
   * Commands bound for the realm, raised from a screen the city is on.
   *
   * The ordinary queue is handed to whichever world is being drawn, which is
   * right for everything the player does with their hands on the map. The
   * Ledger's Powers tab is the exception: it is opened standing in the city and
   * it declares wars, and a declaration handed to a settlement is a declaration
   * dropped on the floor without a word.
   */
  const realmQueue = new CommandQueue();

  /**
   * Orders the *other* crown gave on a field the two of them are standing on.
   *
   * They arrive on a socket callback, which can land at any point in a frame,
   * and they are applied inside the tick loop like every other command. A queue
   * rather than a direct write, for the reason every command in this project is
   * a command: nothing writes to a world except `applyCommand`.
   */
  const fieldQueue = new CommandQueue();
  const drainField = (): Command[] => fieldQueue.drain();

  /**
   * A frame's orders, already sorted by which world they are for.
   *
   * Solo there is one answer — the world on screen — and `tickGame` takes a
   * plain array. In a room the sender's screen is the only thing that knows, so
   * the loop sorts them on the way in.
   */
  interface TickCommands {
    realm: Command[];
    city: Command[];
    /** Orders on a field both crowns are standing on. */
    battle: Command[];
  }

  const manage = new Manage({
    onTaxPolicy: (policy) => queue.push(cmd(Cmd.SetTaxPolicy, policy)),
    onRealmCommand: (c) => realmQueue.push(c),
    onCityCommand: (c) => queue.push(c),
  });

  const hud = new Hud(
    {
      onBuildSelect: (s) => {
        buildSel = s;
      },
      onPowerSelect: () => {
        buildSel = { kind: 'none' };
        hud.clearBuildSelection();
      },
      onCompanySelect: (id, additive) => {
        selected = additive ? toggle(selected, id) : [id];
        hud.setSelection(selected);
      },
      onStartWave: () => queue.push(cmd(Cmd.StartWave)),
      onRecruit: (typeIdx) => queue.push(cmd(Cmd.Recruit, typeIdx)),
      onReinforce: (id) => queue.push(cmd(Cmd.Reinforce, id)),
      onRelicPick: (id) => {
        queue.push(cmd(Cmd.PickRelic, 0, 0, 0, 0, id));
        world.relicOffer = [];
      },
      onEventChoice: (eventId, index) => queue.push(cmd(Cmd.ResolveEvent, index, 0, 0, 0, eventId)),
      onFormation: (f) => {
        for (const id of selected) queue.push(cmd(Cmd.SetFormation, id, f));
      },
      onStance: (s) => {
        for (const id of selected) queue.push(cmd(Cmd.SetStance, id, s));
      },
      onHeroSkill: (id) => queue.push(cmd(Cmd.HeroSkill, 0, 0, 0, 0, id)),
      onHeroRevive: () => queue.push(cmd(Cmd.ReviveHero)),
      onHeroEquip: (uid) => queue.push(cmd(Cmd.EquipItem, uid)),
      onHeroUnequip: (slot) => queue.push(cmd(Cmd.UnequipItem, slot)),
      onHeroReforge: (uid) => queue.push(cmd(Cmd.ReforgeItem, uid)),
      onHeroScrap: (uid) => queue.push(cmd(Cmd.ScrapItem, uid)),
      // The dial has been on the resource strip since the HUD was written and
      // has never turned: `onTaxPolicy` was an optional callback with no
      // supplier, so five notches sat there looking like a control.
      onTaxPolicy: (policy) => queue.push(cmd(Cmd.SetTaxPolicy, policy)),
      onLoadout: (act, typeIdx) => queue.push(cmd(Cmd.BattleLoadout, act, typeIdx)),
      onRepair: (idx, cancel) => queue.push(cmd(Cmd.OrderRepair, idx, cancel ? 1 : 0)),
      onBuildingUpgrade: (idx) => queue.push(cmd(Cmd.UpgradeBuilding, idx)),
      onBuildingDemolish: (tx, ty) => queue.push(cmd(Cmd.Demolish, tx, ty)),
      onBuildingClose: () => {
        selectedBuilding = -1;
      },
      onOpenManage: () => manage.show(),
      // Everything below is a key that already worked, given a button as well.
      onOpenSheet: () => {
        if (hud.modalOpen) hud.hideModal();
        // The character lives in the town he was picked in, and the sheet is
        // reachable from the realm map now — so it is fetched from the town
        // rather than from whatever world is being drawn.
        else hud.showHeroSheet(cityOf() ?? world);
      },
      onTogglePause: () => {
        togglePause();
      },
      onSpeed: (d) => {
        setSpeed(speedIdx + d);
        hud.hint(`Time ×${timescale}`, 1.5);
      },
      onToggleMute: () => {
        applyAudio({ muted: !audio.settings.muted });
        hud.hint(audio.settings.muted ? 'Sound off' : 'Sound on', 2);
      },
      onAudioSettings: (patch) => applyAudio(patch),
      audioSettings: () => ({ ...audio.settings }),
      onDisplaySettings: (patch) => applyDisplay(patch),
      displaySettings: () => ({ shake: scene.camera.shakeScale, numbers: scene.fx.showNumbers }),
      onSwapView: () => {
        if (!campaignWorld) return;
        if (worldmap.isOpen) toCity();
        else toRealm();
      },
    },
    scene,
  );

  const console_ = new DebugConsole((c) => queue.push(c), () => world);

  // Audio cannot start without a gesture, so it waits for the first one.
  const audio = new Audio();
  /**
   * Mix settings survive the session on their own key — a player who set the
   * ambience low on Tuesday has said something they do not want to repeat on
   * Wednesday. Its own key rather than the save: a mix is about the player's
   * room, not about any run.
   */
  const AUDIO_KEY = 'hearth.audio.v1';
  try {
    const saved = JSON.parse(localStorage.getItem(AUDIO_KEY) ?? 'null') as Record<string, unknown> | null;
    if (saved && typeof saved === 'object') {
      const num = (v: unknown): number | undefined =>
        typeof v === 'number' && Number.isFinite(v) ? clamp(v, 0, 1) : undefined;
      audio.setSettings({
        master: num(saved.master) ?? audio.settings.master,
        effects: num(saved.effects) ?? audio.settings.effects,
        ambience: num(saved.ambience) ?? audio.settings.ambience,
        muted: saved.muted === true,
      });
    }
  } catch {
    // A garbled mix loads as the default one.
  }
  function applyAudio(patch: Partial<typeof audio.settings>): void {
    audio.setSettings(patch);
    try {
      localStorage.setItem(AUDIO_KEY, JSON.stringify(audio.settings));
    } catch {
      // Storage off; the mix still applies for the session.
    }
  }
  const wake = () => audio.start();
  window.addEventListener('pointerdown', wake, { once: true });
  window.addEventListener('keydown', wake, { once: true });

  /**
   * Display feel — shake intensity and floating numbers — on its own key,
   * same argument as the mix: it is about the player's eyes, not any run.
   * Both settings existed for a version and were reachable only from code.
   */
  const OPTIONS_KEY = 'hearth.options.v1';
  try {
    const saved = JSON.parse(localStorage.getItem(OPTIONS_KEY) ?? 'null') as Record<string, unknown> | null;
    if (saved && typeof saved === 'object') {
      if (typeof saved.shake === 'number' && Number.isFinite(saved.shake)) {
        scene.camera.shakeScale = clamp(saved.shake, 0, 2);
      }
      if (typeof saved.numbers === 'boolean') scene.fx.showNumbers = saved.numbers;
    }
  } catch {
    // A garbled preference loads as the default one.
  }
  function applyDisplay(patch: { shake?: number; numbers?: boolean }): void {
    if (patch.shake !== undefined) scene.camera.shakeScale = clamp(patch.shake, 0, 2);
    if (patch.numbers !== undefined) scene.fx.showNumbers = patch.numbers;
    try {
      localStorage.setItem(
        OPTIONS_KEY,
        JSON.stringify({ shake: scene.camera.shakeScale, numbers: scene.fx.showNumbers }),
      );
    } catch {
      // Storage off; the preference still applies for the session.
    }
  }

  /**
   * The world map, and the campaign world it belongs to.
   *
   * A campaign that hands a battle to battle mode does not *become* the battle:
   * the campaign world is parked here, untouched, while the tactical world is
   * played, and is picked back up with a `CampaignResolveBattle` when the field
   * is decided. Two worlds, one at a time, and no state copied between them
   * except the result.
   */
  let campaignWorld: World | null = null;
  /**
   * The capital, parked.
   *
   * A campaign is two worlds that are both real and both persist: the realm,
   * and the town standing in the player's home province. Only one is ever
   * ticking, but the other is never discarded — walk out of your city to look
   * at the map and the half-built granary is still half-built when you come
   * back. This is what makes the two halves one game rather than two modes that
   * happen to share a menu.
   */
  let homeWorld: World | null = null;
  /**
   * Every settlement world this crown has, keyed by the province it stands on.
   *
   * *"you can have multiple towns to manage"*. `homeWorld` above is whichever
   * one is currently in hand; this is all of them, the capital included, and it
   * is the thing that is stepped, saved and swapped between. It is empty
   * outside a campaign — a standalone Hold the Hearth is one world with no
   * province under it, and it stays exactly that.
   *
   * A `Map` rather than an array because the key is the province and the
   * question asked of it a hundred times a frame is "is there a town here". Its
   * iteration order is insertion order, which is the order the player entered
   * their towns, so **everything that steps or saves it sorts by province
   * first** — see `townsInOrder`. A tick whose order depends on the order
   * somebody clicked things is a tick a replay cannot reproduce.
   */
  const towns = new Map<number, World>();

  const EMPTY_TOWNS: readonly World[] = [];

  /** Every town but the one on screen, in a fixed order. See `stepGame`. */
  function townsInOrder(): World[] {
    return [...towns.keys()].sort((a, b) => a - b).map((p) => towns.get(p) as World);
  }

  /**
   * The world the character lives in, which is one world and always the seat's.
   *
   * There is one of him — one class, one level, one cart — and `carryCharacterInto`
   * refuses a world that already has a hero in it, so he cannot be in two towns
   * at once. Rather than let "where is he" be whichever town the player last
   * opened, he is at home, and a capital sacked out from under him is a
   * different problem for a different pass (see COORDINATION).
   */
  function heroHome(): World | null {
    const seat = campaignWorld?.campaign?.capital ?? -1;
    return (seat >= 0 ? towns.get(seat) : null) ?? homeWorld;
  }

  /** Which province a settlement world stands on, or -1. */
  function provinceOfTown(w: World | null): number {
    if (!w) return -1;
    if (w.config.province !== undefined) return w.config.province;
    return campaignWorld?.campaign?.capital ?? -1;
  }

  /**
   * Rebuild the town map out of a loaded save.
   *
   * The seat is read off the loaded realm rather than off `campaignWorld`,
   * because this runs before the swap: a save written by a build from before
   * holdings could be entered has one settlement, it carries no
   * `config.province`, and it is the capital — so the fallback is the answer
   * rather than a guess.
   */
  function restoreTowns(g: LoadedGame): void {
    towns.clear();
    if (!g.realm) return;
    const seat = g.realm.campaign?.capital ?? -1;
    for (const w of [...(g.city ? [g.city] : []), ...g.towns]) {
      const p = w.config.province ?? seat;
      if (p >= 0) towns.set(p, w);
    }
  }

  /**
   * Set only while a besieging host is actually inside the walls of the home
   * world. Like a battle, an assault is fought in one sitting and is not part of
   * the saved state — see `doSave`.
   */
  let homeAssault: HomeAssaultSnapshot | null = null;
  let assaultResultShown = false;

  const travel = new Travel({
    onToRealm: () => toRealm(),
    onEnterHold: (p) => enterHold(p),
  });

  /**
   * The realm's chronicle. Answers ride `realmQueue` for the same reason the
   * Ledger's declarations do: the question was put to the crown, and the crown
   * answers it wherever the player happens to be standing.
   */
  const realmEvent = new RealmEventModal({
    onCommand: (c) => realmQueue.push(c),
    onToRealm: () => toRealm(),
  });

  /**
   * The war menu, on whichever screen the player is standing on.
   *
   * Its answers ride `realmQueue` for the same reason the Chronicle's do: the
   * question was put to the crown, and the crown answers it from the city as
   * readily as from the map. Constructed before the map, because the map no
   * longer draws one of its own.
   */
  const warCall = new WarCall({
    onCommand: (c) => realmQueue.push(c),
    onFight: () => enterCampaignBattle(),
  });

  const worldmap = new WorldMap({
    onCommand: (c) => queue.push(c),
    onEnterCity: () => toCity(),
    onEnterHold: (p) => enterHold(p),
    onOpenLedger: () => manage.toggle(),
    onOpenLevies: () => levies.toggle(),
    onQuit: () => {
      worldmap.hide();
      travel.hide();
      campaignWorld = null;
      homeWorld = null;
      towns.clear();
      menu.show();
    },
  });

  /**
   * The Levies — the crown's other account book.
   *
   * Its orders ride `realmQueue` for the reason the Chronicle's and the war
   * menu's do: the queue is routed to whichever world is on screen, and a
   * muster is the crown's business even when the player is standing in a town.
   * Its two other doors go back to the map, because a list of provinces whose
   * rows cannot take you to the ground they name is a list of names.
   */
  const levies = new LevyBoard({
    onRealmCommand: (c) => realmQueue.push(c),
    onLookAt: (p) => {
      const c = realmOf()?.campaign;
      if (c) worldmap.showProvince(c, p);
    },
    onShowMarks: () => {
      const c = realmOf()?.campaign;
      if (c) worldmap.showLevyMarks(c);
    },
  });

  // The lesson cards. A reader over `World.tutorialStep`, which is what the
  // director bends the first five waves against — see `ui/tutorial.ts` and
  // `sim/systems/tutorial.ts`. It needs the queue only to send Skip.
  const tutorial = new Tutorial({ onCommand: (c) => queue.push(c) });
  /**
   * The wait, given a screen.
   *
   * Built here beside the menu because it is the same moment in a player's
   * evening: the title screen goes away, and something has to be there instead
   * until the board arrives. It used to be nothing at all.
   */
  const lobby = new Lobby();

  const menu = new Menu({
    /**
     * The campaign. This is the game: a realm to take, and one city in it that
     * is yours to build. Both worlds are made here, together, and the player
     * starts standing in the city — small, slow, and a long way from a capital
     * worth besieging.
     */
    onCampaign: behindAccount((c) => {
      tutorial.stop();
      const base = {
        seed: c.seed,
        factionId: c.factionId,
        difficultyId: c.difficultyId,
        sandbox: false,
        // The kingdom travels in the config rather than as a queued command:
        // `Cmd.PickKingdom` is refused once the run is under way, and `initRun`
        // folds both identity layers into `w.mods` before the queue is drained.
        // `raceId`/`leaderId` are the door `races` built for exactly this, and
        // both are optional, so every other caller is untouched.
        raceId: c.raceId,
        leaderId: c.kingdomId,
        // The family's unlocks, read from the bank exactly once, at the door.
        bloodline: ownedBloodline(),
      };
      const realm = createCampaignWorld(base);
      /**
       * The capital is built for the ground it actually stands on.
       *
       * `realm.campaign.capital` is a province index and `provinceDef` knows
       * its country, so the settlement generator can be told whether this hold
       * is in a wood, on a mountain shoulder or out in the sand. Read from the
       * realm that was just created rather than from a constant, so a crown
       * seated somewhere else gets somewhere else's ground.
       */
      const seat = realm.campaign?.capital ?? 0;
      const city = createWorld({
        ...base,
        homeCity: true,
        biome: provinceDef(seat).terrain,
        // Which holding this is. The capital is a holding like any other now —
        // it is simply the one the crown starts already standing in.
        province: seat,
      });
      initRun(city);
      campaignWorld = realm;
      homeWorld = city;
      towns.clear();
      towns.set(seat, city);
      adoptWorld(city, city.map.hearthX, city.map.hearthY, 34);
      travel.show();
      // The hero is chosen through the same door as everything else, so a run
      // that begins with a character is still just a seed and a command log.
      // Pushed *after* the adopt, which drains the queue.
      if (c.heroId) queue.push(cmd(Cmd.PickHero, 0, 0, 0, 0, c.heroId));
    }),
    /**
     * A room's settlement. The host's kingdom choice is the room's; allies
     * bring only a name and inherit the recipe down the wire.
     */
    // `startCoop` asks for the account itself — it needs the handle for the
    // seat, not merely permission — so this one is not wrapped.
    onCoop: (o) => {
      void startCoop(o);
    },
    // The same door the in-game panel uses, opened before a run exists.
    onAudioSettings: (patch) => applyAudio(patch),
    audioSettings: () => ({ ...audio.settings }),
    /** A standalone settlement with no realm around it — practice, and the harness's shape. */
    onSiegeOnly: behindAccount((c) => {
      tutorial.stop();
      const next = createWorld({
        seed: c.seed,
        factionId: c.factionId,
        difficultyId: c.difficultyId,
        sandbox: false,
        raceId: c.raceId,
        leaderId: c.kingdomId,
        bloodline: ownedBloodline(),
      });
      initRun(next);
      campaignWorld = null;
      homeWorld = null;
      towns.clear();
      adoptWorld(next, next.map.hearthX, next.map.hearthY, 34);
      travel.hide();
      if (c.heroId) queue.push(cmd(Cmd.PickHero, 0, 0, 0, 0, c.heroId));
    }),
    onBattle: behindAccount((c) => {
      tutorial.stop();
      const next = createBattle({
        seed: c.seed,
        factionId: c.factionId,
        difficultyId: c.difficultyId,
        sandbox: false,
        scenario: c.scenario,
        enemyFactionId: c.enemyFactionId,
        allyFactionId: c.allyFactionId ?? undefined,
        enemyAllyFactionId: c.enemyAllyFactionId ?? undefined,
        points: c.points,
        // The fight game mode, and the only place the muster list opens.
        freeMuster: true,
      });
      campaignWorld = null;
      homeWorld = null;
      towns.clear();
      travel.hide();
      adoptWorld(next, MAP_W / 2, MAP_H / 2 + 6, 20);
    }),
    onContinue: behindAccount((slot: number) => {
      tutorial.stop();
      // Reading a slot makes it the one save and autosave write back to, so a
      // resumed run keeps its own file instead of overwriting slot one.
      const g = readGame(slot);
      if (!g) return;
      restoreTowns(g);
      campaignWorld = g.realm;
      homeWorld = g.city && g.realm ? g.city : null;
      if (g.at === 'realm' && g.realm) {
        adoptWorld(g.realm, MAP_W / 2, MAP_H / 2, 20);
        travel.hide();
        worldmap.show(g.realm);
        return;
      }
      const city = g.city ?? g.realm;
      if (!city) return;
      adoptWorld(city, city.map.hearthX, city.map.hearthY, 34);
      if (campaignWorld) travel.show();
      else travel.hide();
    }),
    /**
     * The guided run. An ordinary Hold the Hearth on Ember with a fixed map —
     * every player's first settlement is the same ground, so a friend's advice
     * about "the forest west of the Hearth" is advice about *your* forest —
     * and the lesson cards watching over it. The cards are a reader; skipping
     * them leaves a normal run.
     */
    onTutorial: behindAccount(() => {
      const next = createWorld({
        seed: seedFromString('the-first-lesson'),
        factionId: 'ashen',
        difficultyId: DIFFICULTIES[0].id,
        sandbox: false,
        bloodline: ownedBloodline(),
      });
      initRun(next);
      campaignWorld = null;
      homeWorld = null;
      towns.clear();
      adoptWorld(next, next.map.hearthX, next.map.hearthY, 34);
      travel.hide();
      /**
       * The lessons are turned on through the same door as everything else, and
       * pushed *after* the adopt, which drains the queue — the `PickHero` trap,
       * one line up in every other entry on this menu. The card needs no start
       * call: it reads `world.tutorialStep`, so it also comes back by itself
       * when a half-finished guided run is loaded from a slot.
       */
      queue.push(cmd(Cmd.TutorialBegin));
    }),
  });

  // ── The road between the realm and the city ────────────────────────────

  /** Park whatever city is on screen and open the map. */
  function toRealm(): void {
    if (!campaignWorld) return;
    // Never mid-assault: walking out of a siege to look at a map would leave
    // the besiegers standing in the streets with nobody simulating them.
    if (homeAssault) {
      hud.hint('Not while they are inside the walls.', 2.5);
      return;
    }
    if (!world.campaign) homeWorld = world;
    const realm = campaignWorld;
    // Hand the realm the city's measure on the way out. This is the only moment
    // both worlds are in hand, and it is what makes a growing city visible to
    // the nations that might want it.
    /**
     * In a room this write goes through the log like everything else.
     *
     * Walking out to the map is a *local* act — one player does it, the other
     * may be laying bricks — but the measure it hands the realm is shared
     * state. Written directly, it desynchronised the room the first time
     * somebody opened the map; sent as a command, every client makes the same
     * write on the same tick.
     */
    if (homeWorld) {
      if (net) {
        const meas = capitalMeasure(homeWorld);
        // The holding this town actually stands on, not the seat. Two towns
        // and one province index is how a captured hamlet makes the capital
        // look poorer every time the player visits it.
        const at = provinceOfTown(homeWorld);
        realmQueue.push(
          cmd(Cmd.SetCapitalStanding, Math.round(meas.development * 1000), meas.fort, at),
        );
        // The garrison half of the same measure. A second command rather than
        // two more numbers on the first: the Age is not a property of a
        // province, and `SetCapitalStanding` already carries all the province
        // terms it has. See `Cmd.CampaignSetWatch`.
        realmQueue.push(cmd(Cmd.CampaignSetWatch, at, meas.barracks, meas.age));
      } else {
        syncCapitalDevelopment(realm, homeWorld);
      }
    }
    travel.hide();
    adoptWorld(realm, MAP_W / 2, MAP_H / 2, 20);
    campaignWorld = realm;
    worldmap.show(realm);
  }

  /** Close the map and stand back up in whichever town was last in hand. */
  function toCity(): void {
    if (!homeWorld) return;
    const city = homeWorld;
    worldmap.hide();
    adoptWorld(city, city.map.hearthX, city.map.hearthY, 34);
    homeWorld = city;
    travel.show();
  }

  /**
   * Walk into a holding of yours — the door the backlog's largest item asked
   * for, and the only new verb in this whole pass.
   *
   * Three cases and they are deliberately one function, because from the
   * player's side they are one act:
   *
   *   • **A town you are already in.** Nothing happens.
   *   • **A town you have entered before.** It is standing where you left it —
   *     it has been ticking the whole time — so this is a camera move and a
   *     screen change, exactly like walking back into the capital.
   *   • **A holding you have never stood in.** The world is generated now, from
   *     the province's own numbers, and kept for the rest of the campaign. See
   *     `campaign/holdworld.ts` for why it is generated here rather than at
   *     capture.
   *
   * **Order matters, and it is the same trap `finishHomeAssault` is written
   * around.** `adoptWorld` drains the command queue, so both commands are
   * pushed *after* the swap: `EnterHold` on the realm's queue, which is drained
   * separately and survives, and `WreckTown` on the town's own, which would
   * otherwise be swallowed by the world we are leaving.
   */
  function enterHold(province: number): void {
    const realm = campaignWorld;
    const c = realm?.campaign;
    if (!realm || !c) return;
    if (homeAssault) {
      hud.hint('Not while they are inside the walls.', 2.5);
      return;
    }
    const why = holdBlock(realm, province);
    if (why) {
      hud.hint(why, 2.5);
      return;
    }
    if (world === towns.get(province)) {
      worldmap.hide();
      travel.show();
      return;
    }

    // On the way out of wherever we are standing, the realm is told what that
    // town has become — the same hand-off `toRealm` makes, and for the same
    // reason: this is the one moment both worlds are in hand.
    if (homeWorld && !net) syncCapitalDevelopment(realm, homeWorld);

    /**
     * The sack, read *before* the world is stood up and paid *after*.
     *
     * `prov.wreck` is a debt recorded at the moment of capture, when there may
     * have been no settlement world here at all. `Cmd.EnterHold` clears it, so
     * it has to be read first or the town would arrive whole and the debt would
     * be cancelled anyway — one sack, silently never paid.
     */
    const owed = Math.round(Math.max(0, Math.min(1, c.provinces[province].wreck)) * 1000);

    let town = towns.get(province);
    if (!town) {
      const seat = towns.get(c.capital) ?? homeWorld;
      const made = createHoldWorld(realm, province, seat ?? null);
      if (!made) {
        hud.hint('There is nothing to stand in there.', 2.5);
        return;
      }
      town = made;
      towns.set(province, town);
    }

    worldmap.hide();
    adoptWorld(town, town.map.hearthX, town.map.hearthY, 34);
    homeWorld = town;
    travel.show();
    // After the adopt, which has just drained both queues.
    realmQueue.push(cmd(Cmd.EnterHold, province));
    if (owed > 0) queue.push(cmd(Cmd.WreckTown, owed));
    hud.hint(
      owed > 0
        ? `${provinceDef(province).name}. Half of it is rubble — put it back.`
        : `${provinceDef(province).name}.`,
      3.5,
    );
  }

  /**
   * Stand the pending battle up and go and fight it.
   *
   * Two roads out of here, and which one is taken is the whole point of the
   * unification: an assault on the player's own capital is fought *in the city
   * they built*, with their walls and their towers on the ground they chose.
   * Everything else is a field.
   */
  function enterCampaignBattle(): void {
    if (!campaignWorld) return;
    const c = campaignWorld.campaign;
    const pending = c?.pending;
    if (!c || !pending || !pending.playerFights) return;

    const defendingHome = isHomeAssault(c);
    /**
     * …and it is fought in the town they are actually at, not in the capital.
     *
     * `isHomeAssault` now says "there is a town of mine standing here", which
     * may be any of several. Reading `homeWorld` — whichever town happened to
     * be in hand — would have stood the besiegers up in the *wrong city*: the
     * player's capital, with their capital's walls, while the province actually
     * under attack changed hands on the map behind them.
     *
     * The world must already exist, and it does: `settled` is only ever written
     * by `Cmd.EnterHold`, which is pushed by the same act that creates the
     * world. A hold the player has never entered is not a home assault and is
     * resolved as a field, exactly as every province was before this pass.
     */
    const attacked = defendingHome ? (towns.get(pending.province) ?? null) : null;
    if (defendingHome && attacked) {
      const host = findArmy(c, pending.attackerArmy);
      if (host) {
        const nation = c.nations[pending.attackerNation];
        const parked = campaignWorld;
        const city = attacked;
        worldmap.hide();
        adoptWorld(city, city.map.hearthX, city.map.hearthY, 30);
        campaignWorld = parked;
        homeWorld = city;
        travel.show();
        assaultResultShown = false;
        homeAssault = beginHomeAssault(city, {
          nation: pending.attackerNation,
          factionId: nationDef(nation.def).factionId,
          nationName: nationDef(nation.def).name,
          regiments: host.regiments.map((r) => ({ ...r })),
          /**
           * Whatever the player put inside these walls comes out and fights.
           *
           * Read off the campaign at the moment the horns go rather than
           * carried on the pending record, because the pending record names one
           * defending army and a player may have put two hosts behind the same
           * gate. Copied, because the city world is about to be simulated for
           * twenty minutes and the campaign's own regiments must not move with
           * it — the losses come back through `Cmd.CampaignResolveBattle` like
           * every other fight's.
           */
          defenders: shelteredIn(c, pending.province).flatMap((a) =>
            a.owner === c.provinces[pending.province].owner
              ? a.regiments.map((r) => ({ ...r }))
              : [],
          ),
        });
        return;
      }
    }

    const cfg = battleConfigFor(campaignWorld);
    if (!cfg) return;
    // On a shared field the attacker's colours are canonical, so a defending
    // client commands the other side. Everywhere that asks "is this mine"
    // asks this rather than assuming Team.Player.
    const takeTeam = myBattleTeam(campaignWorld);
    const parked = campaignWorld;
    worldmap.hide();
    travel.hide();
    const field = createBattle(cfg);
    // The character rides out with the army. He lives in the capital's world —
    // that is where he was picked and where his cart is kept — so that is where
    // he is fetched from, and with several towns "the capital" has to be said
    // rather than assumed to be whichever one is in hand. See
    // `carryCharacterInto`, and `heroHome` for why there is one of him.
    const hh = heroHome();
    if (hh) carryCharacterInto(field, hh);
    adoptWorld(field, MAP_W / 2, MAP_H / 2 + 6, 20);
    // After the adopt, which has just reset it.
    myTeam = takeTeam;
    campaignWorld = parked;
  }

  /**
   * The capital held, or it did not. Report it in the same two numbers a field
   * battle reports, so the campaign cannot tell which kind of fight it was.
   */
  function finishHomeAssault(snap: HomeAssaultSnapshot, res: ReturnType<typeof homeAssaultResult>): void {
    myTeam = Team.Player;
    const parked = campaignWorld;
    homeAssault = null;
    if (!parked) return;
    const accent = nationAccent(parked.campaign ? parked.campaign.nations[snap.assault.nation].def : 0);
    hud.showSheet(
      `<h1>${res.playerWon ? 'The Walls Hold' : 'The Hearth Goes Out'}</h1>` +
        `<div class="sub" style="color:${toCss(accent)}">${esc(snap.assault.nationName)} — ` +
        // The town that was actually assaulted, which `world` is standing in.
        // It read `campaign.capital` and would now name the wrong city on any
        // fight but one.
        `${esc(provinceDef(provinceOfTown(world) >= 0 ? provinceOfTown(world) : (parked.campaign?.capital ?? 0)).name)}</div>` +
        `<div class="cr">${esc(assaultSummary(world, snap))}</div>` +
        `<div style="margin-top:18px"><span class="seal" id="backtomap">To the Realm</span></div>`,
    );
    const btn = document.getElementById('backtomap');
    if (btn) {
      btn.onclick = () => {
        hud.hideModal();
        // Order matters and has bitten before: `adoptWorld` drains the command
        // queue, so the verdict has to be pushed *after* the realm is the world
        // that is running. Pushed before, the city would swallow a command it
        // has no campaign to apply and the map would never hear the result.
        toRealm();
        const perMille = (f: number) => Math.round(Math.max(0, Math.min(1, f)) * 1000);
        queue.push(
          cmd(
            Cmd.CampaignResolveBattle,
            res.playerWon ? 1 : -1,
            perMille(res.playerSurvive),
            perMille(res.enemySurvive),
          ),
        );
      };
    }
  }

  /**
   * Hand the field's verdict back to the map. Survivors go over as permille of
   * each side's starting models, which is the one number the campaign needs and
   * the only one battle mode can honestly report.
   */
  function returnToCampaign(): void {
    const b = world.battle;
    const parked = campaignWorld;
    if (!parked || !b) return;
    const mine = armyStrength(world, b.player);
    const theirs = armyStrength(world, b.enemy);
    const perMille = (s: { alive: number; total: number }) =>
      Math.round((s.alive / Math.max(1, s.total)) * 1000);
    // What he learned out there, and what he picked up off the ground, comes
    // home with him. His body and his cooldowns stay on the field.
    const hh = heroHome();
    if (hh) takeCharacterHome(world, hh);
    adoptWorld(parked, MAP_W / 2, MAP_H / 2, 20);
    campaignWorld = parked;
    queue.push(cmd(Cmd.CampaignResolveBattle, b.outcome > 0 ? 1 : -1, perMille(mine), perMille(theirs)));
    worldmap.show(parked);
  }

  // ── Input ──────────────────────────────────────────────────────────────
  const keys = new Set<string>();
  // Start at the centre of the screen, not (0, 0) — otherwise the edge-pan
  // check fires immediately and the camera drifts into the top-left corner
  // before the player has touched anything.
  let mouseX = window.innerWidth / 2;
  let mouseY = window.innerHeight / 2;
  let mouseInside = false;
  let showFps = false;
  /**
   * Speed, and the pause.
   *
   * A long game in real time needs both, and neither is simulation state: the
   * world only ever advances in whole 30 Hz ticks, so "faster" means feeding the
   * accumulator more of them per second and "paused" means feeding it none. A
   * session played at ×8 produces exactly the command log a session played at ×1
   * would, which is what makes a speed control safe to have at all.
   *
   * A ladder rather than a continuous dial, because the useful question is "is
   * this the slow one or the fast one" and a player should be able to answer it
   * from the number without doing arithmetic.
   */
  const SPEEDS = [0.5, 1, 2, 4, 8];
  let speedIdx = 1;
  let timescale = SPEEDS[speedIdx];
  let paused = false;
  /**
   * The co-op session, when `?mp=1` or the lobby asked for one.
   *
   * In a room this file simulates strictly less than it does alone: the
   * settlement is still its own, and the realm is the server's — received
   * rather than stepped. Everything else treats a net game as an ordinary run
   * whose speed dial happens to be welded to ×1, because the realm's clock is
   * the server's and a client running its city at ×8 would be a player living
   * eight times faster than the war around them.
   */
  let net: Session | null = null;
  /**
   * The armies on the road to *your* gates, as of the last frame.
   *
   * The one thing real time takes away that turns gave for free is the promise
   * that you cannot miss something. `warnings.ts` still guarantees a month of
   * notice, but a month at ×8 is thirty-five seconds and the player is not eight
   * times faster. So the game drops itself back to walking pace when a host takes
   * the road to the capital — the single warning that must not go by unread.
   */
  let capitalWarnKey = '';
  /** The council sitting `capitalWarnKey` was read at. -1 = never. */
  let lastWarnTurn = -1;

  /**
   * Pause, alone or together.
   *
   * In a room the pause is asked for rather than taken: the realm is stepped by
   * the server, so the only thing that can actually stop it is the server. The
   * request goes out, the answer comes back to every seat, and the hint says
   * whose it was.
   */
  function togglePause(): void {
    if (net) {
      /**
       * Asked for, not taken, and the room may well say no.
       *
       * Deliberately sent even when this seat is not the host. A client that
       * silently declines to send a message it thinks it is not allowed to send
       * is a client teaching the player that the key is broken; the server
       * answers with a sentence naming the rule, `onRefusal` prints it, and the
       * room is told that somebody asked. The rule lives on the machine holding
       * the realm — this is a keypress, not a permission check.
       */
      net.setPaused(!net.paused);
      return;
    }
    paused = !paused;
    hud.hint(paused ? `Paused — ${keyLabel(KEY_PAUSE)} to continue` : '');
  }

  /** The colours opposite mine, for reading an order aimed at a company. */
  const enemyOf = (t: number): number => (t === Team.Player ? Team.Invader : Team.Player);

  /**
   * Which colours this client commands on the field it is standing on.
   *
   * `Team.Player` everywhere except a battle between two crowns, where the
   * board decides the sides and one of the two players is commanding what
   * the world calls the enemy.
   */
  let myTeam: number = Team.Player;

  function setSpeed(i: number): void {
    // A co-op run has one clock between everyone; the dial stays at x1.
    if (net) return;
    speedIdx = clamp(Math.round(i), 0, SPEEDS.length - 1);
    timescale = SPEEDS[speedIdx];
  }

  let dragStart: { x: number; y: number; wx: number; wy: number } | null = null;
  let boxSelecting = false;
  let rightDrag: { wx: number; wy: number } | null = null;
  let wallDragStart: { x: number; y: number } | null = null;

  const canvas = app.canvas;
  canvas.addEventListener('contextmenu', (e) => e.preventDefault());

  window.addEventListener('resize', () => {
    scene.resize(app.screen.width, app.screen.height);
  });

  /**
   * A save on the desktop is several megabytes going to a disk, and the window
   * can be closed while it is still going. There is no way to await inside this
   * event, so the only honest thing available is to ask — and only when bytes
   * really are in flight, which is a fraction of a second after each autosave
   * and never otherwise. In a browser `savesPending()` is always false, so this
   * listener does nothing there, which is the point.
   */
  window.addEventListener('beforeunload', (e) => {
    if (!savesPending()) return;
    e.preventDefault();
    e.returnValue = '';
  });

  window.addEventListener('keydown', (e) => {
    if (console_.open) {
      if (e.key === 'Escape' || e.key === KEY_CONSOLE) console_.toggle();
      return;
    }
    /**
     * A field being typed into owns the keyboard.
     *
     * Every hotkey here is a bare letter, so the moment the game grew a text
     * field — a name to be known by in a room — the two collided: typing a
     * name with a `p` in it paused the game and swallowed the letter, and `m`
     * muted the sound, and `l` opened the Ledger. Nothing in this handler is
     * worth having while somebody is writing.
     */
    const el = e.target as HTMLElement | null;
    if (
      el &&
      (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)
    ) {
      return;
    }
    const k = e.key.toLowerCase();
    keys.add(k);

    if (k === KEY_CONSOLE) {
      console_.toggle();
      e.preventDefault();
      return;
    }
    if (k === 'escape') {
      // The Ledger is a screen, so it eats the first Escape and nothing else
      // does: closing it and dropping the player's selection in one press is
      // two things happening for one key.
      if (manage.isOpen) {
        manage.hide();
        return;
      }
      // The Levies is a screen on the same terms, and it is over the map, so it
      // has to take the key before the map's own deselect does.
      if (levies.isOpen) {
        levies.hide();
        return;
      }
      /**
       * The realm holds its own selection, so it gets the key before the
       * settlement's does.
       *
       * `escape` returns whether it used it: the first press lets go of the
       * host, the province and whatever the column was following, and only a
       * press with nothing left to let go of falls through to the rules below.
       * A key that means both "deselect" and "leave" has to spend itself on the
       * smaller meaning first, or clicking one province on a three-screen board
       * is a thing you cannot undo without closing the map.
       */
      if (worldmap.isOpen && world.campaign && worldmap.escape(world.campaign)) return;
      /**
       * Escape with nothing to dismiss is the way out of the game.
       *
       * It clears a selection first, because that is what a player means by it
       * ninety times in a hundred — but once there is nothing left to clear,
       * the hundredth meaning is "I want to stop", and until now the only
       * answer was the browser's close button.
       */
      const nothingToClear =
        selected.length === 0 &&
        selectedBuilding < 0 &&
        buildSel.kind === 'none' &&
        !hud.modalOpen &&
        !world.pendingEvent;
      if (nothingToClear && !menu.isOpen) {
        showPauseMenu();
        return;
      }
      buildSel = { kind: 'none' };
      hud.clearBuildSelection();
      hud.clearArmedPower();
      selected = [];
      hud.setSelection(selected);
      selectedBuilding = -1;
      hud.setBuildingSelection(-1);
      // Escape closes anything except the Chronicle. That one has no way out
      // but through, and the frame loop would only put it straight back up.
      if (!world.pendingEvent) hud.hideModal();
      return;
    }
    /**
     * Home — and Backspace, which is the one Paradox has used for this since
     * the first of these games — puts the view back on your own seat.
     *
     * Only while the realm is up: in the settlement the camera has WASD, the
     * arrows and the screen edge, and there is one town to be lost in.
     */
    if ((k === 'home' || k === 'backspace') && worldmap.isOpen && world.campaign) {
      worldmap.home(world.campaign);
      e.preventDefault();
      return;
    }
    if (k === KEY_STATS) {
      showFps = !showFps;
      e.preventDefault();
      return;
    }
    if (k === KEY_SAVE) {
      doSave();
      e.preventDefault();
      return;
    }
    if (k === KEY_LOAD) {
      doLoad();
      e.preventDefault();
      return;
    }
    /**
     * The Ledger. L is free of everything: the camera has WASD and the arrows,
     * the Powers have Q/E/R/T/Y/U/Z/X/C/N, the company orders have F/G/H/V/B,
     * and the digits are the hero's.
     */
    if (k === KEY_LEDGER) {
      manage.toggle();
      return;
    }
    /**
     * The Levies, on the realm map only.
     *
     * Unlike the Ledger this screen is about a crown's ground rather than about
     * a town, and outside a campaign there is no crown — so the key does
     * nothing there rather than opening a sheet with nothing on it.
     */
    if (k === KEY_LEVIES && worldmap.isOpen && world.campaign) {
      levies.toggle();
      return;
    }
    if (k === KEY_MUTE) {
      applyAudio({ muted: !audio.settings.muted });
      hud.hint(audio.settings.muted ? 'Sound off' : 'Sound on', 2);
      return;
    }
    if (k === KEY_PAUSE) {
      togglePause();
      e.preventDefault();
      return;
    }
    if (k === KEY_FASTER || k === '=') {
      setSpeed(speedIdx + 1);
      hud.hint(`Time ×${timescale}`, 1.5);
      return;
    }
    if (k === KEY_SLOWER || k === '_') {
      setSpeed(speedIdx - 1);
      hud.hint(`Time ×${timescale}`, 1.5);
      return;
    }
    if (k === KEY_HORN) {
      queue.push(cmd(Cmd.StartWave));
      e.preventDefault();
      return;
    }
    if (k === KEY_SWAP) {
      e.preventDefault();
      /**
       * In a campaign, Tab is the strategic/tactical toggle every game of this
       * shape has trained players to expect: it moves between the realm and the
       * city. Outside a campaign there is nowhere to go, so it keeps its older
       * job of snapping the camera between tactical and close zoom — which is
       * still reachable in a campaign on the wheel.
       */
      if (campaignWorld) {
        if (worldmap.isOpen) toCity();
        else toRealm();
        return;
      }
      scene.camera.setZoom(scene.camera.zoom > ZOOM_TACTICAL * 1.6 ? ZOOM_TACTICAL : 36);
      return;
    }

    // Powers.
    const pi = POWER_HOTKEYS.indexOf(k);
    if (pi >= 0 && pi < world.unlockedPowers.length) {
      hud.armPower(world.unlockedPowers[pi]);
      buildSel = { kind: 'none' };
      hud.clearBuildSelection();
      return;
    }

    // The hero: battle skills, and the sheet.
    if (k === KEY_SHEET) {
      if (hud.modalOpen) hud.hideModal();
      else hud.showHeroSheet(world);
      return;
    }
    /**
     * The digits.
     *
     * Ctrl+digit sets a company group and Shift+digit recalls one; a bare digit
     * fires the hero's battle skill in that slot. The slots are fixed by the
     * level that unlocks them, so a key means the same thing all run — and a
     * key whose skill is not learned yet says what it is waiting for rather
     * than silently doing nothing.
     */
    if (/^[0-9]$/.test(k)) {
      const n = Number(k);
      if (e.ctrlKey || e.metaKey) {
        if (n >= 1) hotkeyGroups.set(n, selected.slice());
        return;
      }
      if (e.shiftKey) {
        if (n < 1) return;
        const g = hotkeyGroups.get(n);
        if (g && g.length > 0) {
          selected = g.slice();
        } else {
          const list = world.companies.filter((c) => c.team === myTeam && !c.dead);
          if (list[n - 1]) selected = [list[n - 1].id];
        }
        hud.setSelection(selected);
        return;
      }

      const hi = HERO_HOTKEYS.indexOf(k);
      if (hi < 0) return;
      const slots = heroAbilitySlots(world);
      const slot = slots[hi];
      if (!slot) {
        if (world.hero.classId !== '') hud.hint('No skill in that slot.', 2);
        return;
      }
      if (!slot.learned) {
        hud.hint(
          slot.reached
            ? `${slot.name} — unlearned. Spend a point on the character sheet (${keyLabel(KEY_SHEET)}).`
            : `${slot.name} — locked until level ${slot.minLevel}.`,
          3,
        );
        return;
      }
      hud.armHeroAbility(slot.powerId);
      buildSel = { kind: 'none' };
      hud.clearBuildSelection();
      return;
    }

    // Formations and stances.
    if (k === KEY_FORMATION) {
      for (const id of selected) {
        const c = world.companies.find((cc) => cc.id === id);
        if (c) queue.push(cmd(Cmd.SetFormation, id, (c.formation + 1) % 4));
      }
    }
    if (k === KEY_STANCE) {
      for (const id of selected) {
        const c = world.companies.find((cc) => cc.id === id);
        if (c) queue.push(cmd(Cmd.SetStance, id, (c.stance + 1) % 4));
      }
    }
    if (k === KEY_HOLD) for (const id of selected) queue.push(cmd(Cmd.SetStance, id, Stance.Hold));
    if (k === KEY_SHIELDWALL) {
      for (const id of selected) queue.push(cmd(Cmd.SetFormation, id, Formation.Shieldwall));
    }
    if (k === KEY_RALLY) for (const id of selected) queue.push(cmd(Cmd.SetStance, id, Stance.Rally));
  });

  window.addEventListener('keyup', (e) => keys.delete(e.key.toLowerCase()));

  canvas.addEventListener('mouseleave', () => {
    mouseInside = false;
  });
  canvas.addEventListener('mousemove', (e) => {
    mouseX = e.clientX;
    mouseY = e.clientY;
    mouseInside = true;
    hud.onMouseMove(e);
    if (dragStart && !boxSelecting) {
      const d = Math.hypot(e.clientX - dragStart.x, e.clientY - dragStart.y);
      if (d > 6) boxSelecting = true;
    }
  });

  canvas.addEventListener('mousedown', (e) => {
    const wx = scene.camera.screenToWorldX(e.clientX);
    const wy = scene.camera.screenToWorldY(e.clientY);

    if (e.button === 0) {
      // Loose an armed hero skill. Cast from where he is, not from above.
      if (hud.armedHero) {
        queue.push(cmd(Cmd.HeroAbility, wx, wy, 1, 0, hud.armedHero));
        if (!e.shiftKey) hud.clearArmedHeroAbility();
        return;
      }
      // Cast an armed power.
      if (hud.armed) {
        const def = powerOrNull(hud.armed);
        if (def) {
          const dx = Math.cos(0);
          const dy = Math.sin(0);
          queue.push(cmd(Cmd.CastPower, wx, wy, dx, dy, hud.armed));
          if (!e.shiftKey) hud.clearArmedPower();
        }
        return;
      }
      // Place, or begin a drag-line.
      if (buildSel.kind === 'building') {
        queue.push(cmd(Cmd.Build, buildSel.typeIdx, Math.floor(wx), Math.floor(wy)));
        if (!e.shiftKey) {
          buildSel = { kind: 'none' };
          hud.clearBuildSelection();
        }
        return;
      }
      if (
        buildSel.kind === 'wall' ||
        buildSel.kind === 'gate' ||
        buildSel.kind === 'road' ||
        buildSel.kind === 'bridge'
      ) {
        /**
         * Ctrl with the wall tool armed raises a **tower** instead of dragging a
         * line.
         *
         * A modifier on the tool that is already selected rather than a button
         * of its own, deliberately: the build menu lives in `ui/hud.ts`, and a
         * tower is not a separate decision from a wall — you are already looking
         * at your line when you decide it wants a tower in it. The hint bar says
         * so while the tool is up, which is where a player looks for exactly
         * this.
         */
        if (buildSel.kind === 'wall' && (e.ctrlKey || e.metaKey)) {
          queue.push(towerCommandAt(wx, wy));
          return;
        }
        wallDragStart = { x: Math.floor(wx), y: Math.floor(wy) };
        return;
      }
      if (buildSel.kind === 'demolish') {
        queue.push(cmd(Cmd.Demolish, Math.floor(wx), Math.floor(wy)));
        return;
      }
      dragStart = { x: e.clientX, y: e.clientY, wx, wy };
    } else if (e.button === 2) {
      if (buildSel.kind !== 'none' || hud.armed || hud.armedHero) {
        buildSel = { kind: 'none' };
        hud.clearBuildSelection();
        hud.clearArmedPower();
        return;
      }
      rightDrag = { wx, wy };
    }
  });

  // On the window, not the canvas. A drag that ends with the pointer over a HUD
  // panel — which is most box-selects that reach the top of the screen, and
  // every wall drag that finishes over the build menu — never fired `mouseup`
  // on the canvas, so the drag stayed live and the next click completed it from
  // wherever the pointer happened to be.
  window.addEventListener('mouseup', (e) => {
    const wx = scene.camera.screenToWorldX(e.clientX);
    const wy = scene.camera.screenToWorldY(e.clientY);

    if (e.button === 0) {
      if (wallDragStart) {
        const kind = buildSel.kind;
        const c =
          kind === 'road'
            ? cmd(Cmd.RoadLine, wallDragStart.x, wallDragStart.y, Math.floor(wx), Math.floor(wy))
            : kind === 'bridge'
            ? cmd(Cmd.BridgeLine, wallDragStart.x, wallDragStart.y, Math.floor(wx), Math.floor(wy))
            : cmd(
                Cmd.WallLine,
                wallDragStart.x,
                wallDragStart.y,
                Math.floor(wx),
                Math.floor(wy),
                kind === 'gate' ? 'gate' : '',
              );
        queue.push(c);
        wallDragStart = null;
        return;
      }
      if (dragStart) {
        if (boxSelecting) {
          selected = companiesInBox(world, dragStart.wx, dragStart.wy, wx, wy, myTeam);
        } else {
          const hit = companyAt(world, wx, wy, myTeam);
          if (hit >= 0) {
            selected = e.shiftKey ? toggle(selected, hit) : [hit];
          } else {
            if (!e.shiftKey) selected = [];
            /**
             * Nothing of yours under the pointer — so ask what building is.
             *
             * Companies first, and not only because they move: a soldier
             * standing on his own barracks is the thing the player meant to
             * click nineteen times out of twenty. A click on bare ground closes
             * the panel, which is the behaviour every game of this shape has.
             */
            const b = scene.buildingAt(world, e.clientX, e.clientY);
            selectedBuilding = b;
            hud.setBuildingSelection(b);
            if (b >= 0) {
              buildSel = { kind: 'none' };
              hud.clearArmedPower();
            } else if (buildSel.kind === 'none') {
              /**
               * No building — but the ground may remember one.
               *
               * `removeBuilding` leaves rubble that carries the type that stood
               * there, and `world.ts` says why in as many words: "the rubble
               * remembers what stood there so rebuilding is one click rather
               * than a hunt". This is that click, and it is a click and not a
               * confirm on purpose — the outline is a deliberate target, the
               * price is the building's own, and a dialogue over a woodcutter's
               * camp is worse than the occasional misclick.
               *
               * Gated on having no build selection armed, so a player who is
               * mid-placement puts the thing they chose on the rubble rather
               * than having the ground choose for them.
               */
              const tx = Math.floor(scene.camera.screenToWorldX(e.clientX));
              const ty = Math.floor(scene.camera.screenToWorldY(e.clientY));
              const r = rubbleReadout(world, tx, ty);
              if (r) {
                if (r.refusal === '') {
                  queue.push(cmd(Cmd.RebuildRubble, tx, ty));
                  scene.log.push({ text: `Raising the ${r.name} again.`, tone: 0, t: 9 });
                } else {
                  scene.log.push({ text: `${r.name}: ${r.refusal}`, tone: 2, t: 9 });
                }
              }
            }
          }
        }
        hud.setSelection(selected);
        dragStart = null;
        boxSelecting = false;
      }
    } else if (e.button === 2 && rightDrag) {
      const dx = wx - rightDrag.wx;
      const dy = wy - rightDrag.wy;
      const len = Math.hypot(dx, dy);
      const attackMove = e.shiftKey || keys.has('shift');

      /**
       * Right-click your own wall, and the company gets on it.
       *
       * The RTS idiom for "act on the thing under the cursor", and it needs no
       * button, no tool and no HUD change — which matters, because a wall is
       * something you point at with the camera already looking at it. A plain
       * move order anywhere else takes them back off again; see the
       * `Cmd.MoveCompany` handler.
       */
      /**
       * Right-click an enemy company, and yours goes for it.
       *
       * The same idiom as the wall: act on the thing under the cursor. Until
       * now every order was a place, and "nearest enemy" decided the rest —
       * so a player could point at ground but never at the ram, the engine or
       * the company that actually had to break. This is that order. Holding
       * position with it still advances the line, because a company told to
       * kill something has been told to reach it.
       */
      if (len <= 1.2 && selected.length > 0) {
        const foe = companyAt(world, wx, wy, world.battle?.pvp ? enemyOf(myTeam) : Team.Invader);
        if (foe >= 0) {
          for (const id of selected) {
            queue.push(cmd(Cmd.AttackCompany, id, foe));
            queue.push(cmd(Cmd.MoveCompany, id, Math.round(wx * 64), Math.round(wy * 64), 1));
          }
          rightDrag = null;
          return;
        }
      }

      if (len <= 1.2 && selected.length > 0 && wallUnder(wx, wy) >= 0) {
        for (const id of selected) {
          queue.push(cmd(Cmd.Garrison, id, Math.floor(wx), Math.floor(wy)));
        }
        rightDrag = null;
        return;
      }

      // Any ordinary move lifts a kill order: the player has changed their
      // mind about where the company should be, which answers what it fights.
      if (selected.length > 0) {
        for (const id of selected) queue.push(cmd(Cmd.AttackCompany, id, -1));
      }

      if (len > 1.2 && selected.length > 0) {
        // Drag order: the line you pull out is the front rank, and the company
        // faces perpendicular to it, away from where you started the drag.
        const mx = rightDrag.wx + dx / 2;
        const my = rightDrag.wy + dy / 2;
        const facing = Math.atan2(dx, -dy);
        for (const id of selected) {
          const c = world.companies.find((cc) => cc.id === id);
          if (!c) continue;
          const spacing = 0.72;
          const width = clamp(Math.round(len / spacing), 1, c.maxSize);
          queue.push(cmd(Cmd.MoveCompany, id, mx, my, attackMove ? 1 : 0));
          queue.push(cmd(Cmd.SetFacing, id, facing, width));
        }
      } else {
        for (const id of selected) queue.push(cmd(Cmd.MoveCompany, id, wx, wy, attackMove ? 1 : 0));
      }
      rightDrag = null;
    }
  });

  canvas.addEventListener('wheel', (e) => {
    scene.camera.zoomBy(e.deltaY < 0 ? 1.16 : 1 / 1.16, e.clientX, e.clientY);
    e.preventDefault();
  }, { passive: false });

  /**
   * Fingers.
   *
   * Bound unconditionally rather than behind a device check: a laptop with a
   * touchscreen is a real machine and gets both, and `TouchInput` only ever
   * reacts to touch events, which a mouse does not send. It translates into the
   * very handlers above rather than reimplementing any of them — see the note
   * at the top of `ui/touch.ts` for why that is the whole point of it.
   */
  new TouchInput(canvas as HTMLCanvasElement, scene.camera, {
    drawing: () =>
      buildSel.kind === 'wall' ||
      buildSel.kind === 'gate' ||
      buildSel.kind === 'road' ||
      buildSel.kind === 'bridge',
  });

  // ── Loop ───────────────────────────────────────────────────────────────
  let acc = 0;
  let fps = 60;
  let lastRelicOffer = 0;
  let shownEventKey = '';
  let battleOutcomeShown = false;
  let ambientTimer = 12;
  /**
   * Autosave bookkeeping, declared *here* rather than beside `maybeAutosave`.
   * `adoptWorld` resets it, and `adoptWorld` runs during boot for any deep link
   * — so a declaration further down the function left it in the temporal dead
   * zone and `?mode=battle` threw before the first frame. It has almost
   * certainly been broken since deep links were added; the screenshot pass is
   * what caught it, which is what the screenshot pass is for.
   */
  let lastAutosaveWave = -1;
  let lastAutosaveTurn = -1;

  /** Distance attenuation from the camera centre, so a far fight is a far fight. */
  function audioGain(x: number, y: number): number {
    const cam = scene.camera;
    const b = cam.viewBounds(6);
    if (x < b.x0 || x > b.x1 || y < b.y0 || y > b.y1) return 0;
    const d = Math.hypot(x - cam.x, y - cam.y);
    const reach = Math.max(12, cam.viewW / cam.effectiveZoom);
    return clamp(1 - d / reach, 0, 1) ** 1.4;
  }

  /** Left of the camera is left of the ear. The synth softens the extremes. */
  function audioPan(x: number, _y: number): number {
    const cam = scene.camera;
    const half = Math.max(6, cam.viewW / cam.effectiveZoom / 2);
    return clamp((x - cam.x) / half, -1, 1);
  }

  /**
   * Deep links: `?mode=campaign` or `?mode=battle&scenario=assault` skip the
   * menu. The screenshot harness uses this, and so can a bookmark.
   *
   * They skip the *menu*, not the door. A hearth that asks for accounts is asked
   * here too, or a link would be the way around a gate every button obeys — and
   * a gate with a documented hole in it is worse than none, because everyone
   * believes the first half of the sentence. The harness is untouched: it runs
   * against a build with no server to ask, so `configured` is false and this
   * costs nothing.
   */
  const mode = params.get('mode');
  if (mode && signIn.configured && !(await requireAccount())) {
    menu.show();
  } else if (mode === 'campaign') {
    const hero = params.get('hero');
    if (hero) queue.push(cmd(Cmd.PickHero, 0, 0, 0, 0, hero));
    menu.hide();
  } else if (mode === 'realm' || mode === 'game') {
    // The whole game from a link: a realm, and the capital standing in it.
    // `?mode=realm` opens on the map, `?mode=game` opens in the city.
    const base = {
      seed,
      factionId: params.get('faction') ?? 'ashen',
      difficultyId: params.get('difficulty') ?? 'endless',
      sandbox: false,
      bloodline: ownedBloodline(),
    };
    const realm = createCampaignWorld(base);
    const city = createWorld({
      ...base,
      homeCity: true,
      /**
       * `?biome=ice` overrides the capital's own country.
       *
       * There is no other way to see a hold in the snow without first
       * conquering the Hoarfast, and there are ten countries against ten
       * playable capitals that between them cover five. Both halves read
       * `config.biome` — the generator bends its terrain thresholds by it and
       * the renderer paints the ground from it — so this one parameter is the
       * whole of the wiring, and the screenshot harness compares one seed
       * across all ten with it.
       */
      biome: params.get('biome') ?? provinceDef(realm.campaign?.capital ?? 0).terrain,
    });
    initRun(city);
    campaignWorld = realm;
    homeWorld = city;
    if (mode === 'game') {
      adoptWorld(city, city.map.hearthX, city.map.hearthY, 34);
      homeWorld = city;
      travel.show();
    } else {
      adoptWorld(realm, MAP_W / 2, MAP_H / 2, 20);
      campaignWorld = realm;
      worldmap.show(realm);
    }
    menu.hide();
  } else if (mode === 'lessons') {
    /**
     * The guided run from a link — the same standalone settlement and the same
     * `Cmd.TutorialBegin` the menu's Learn the Game button makes, so a
     * screenshot of this is a screenshot of the real thing rather than of a
     * mock-up. `?lesson=3` opens at a given lesson, which is what lets the
     * harness photograph all five without fighting four waves for the last one.
     *
     * A *standalone* settlement and not `?mode=game`: that link builds a
     * campaign capital, and a capital has no clock and no director, so
     * `beginGuidedRun` rightly refuses it — the lessons are five waves, and a
     * capital's waves arrive from the strategic map.
     */
    const next = createWorld({
      seed,
      factionId: params.get('faction') ?? 'ashen',
      difficultyId: params.get('difficulty') ?? DIFFICULTIES[0].id,
      sandbox: false,
      bloodline: ownedBloodline(),
    });
    initRun(next);
    campaignWorld = null;
    homeWorld = null;
    adoptWorld(next, next.map.hearthX, next.map.hearthY, 34);
    travel.hide();
    queue.push(cmd(Cmd.TutorialBegin, Number(params.get('lesson') ?? 1) || 1));
    menu.hide();
  } else if (mode === 'battle') {
    /** `tier,fort,capital` off the query string, or nothing. See below. */
    const townParam = (raw: string | null): BattleConfig['town'] => {
      if (!raw) return undefined;
      const parts = raw.split(',').map((v) => Number(v));
      if (parts.length < 2 || parts.some((v) => !Number.isFinite(v))) return undefined;
      return { tier: parts[0], fort: parts[1], capital: parts[2] === 1 };
    };
    const next = createBattle({
      seed,
      factionId: params.get('faction') ?? 'ashen',
      difficultyId: params.get('difficulty') ?? 'endless',
      sandbox: false,
      scenario: (params.get('scenario') as BattleConfig['scenario']) ?? 'open_field',
      enemyFactionId: params.get('enemy') ?? 'ironfang',
      // `?ally=vigil&enemyAlly=rotmoot` makes it a 2v2. Both or neither, the
      // same rule the menu's toggle keeps — a field with one crown short is a
      // 2v1, which the simulation will field and nobody asked for.
      allyFactionId: params.get('ally') ?? undefined,
      enemyAllyFactionId: params.get('enemyAlly') ?? undefined,
      points: Number(params.get('points') ?? 1600),
      /**
       * `?biome=ice` themes the *field*, exactly as it themes a capital nine
       * hundred lines above. `src/sim/field.ts` reads it — mountains get real
       * relief, the sand sea gets dunes and no wood, the fens get water and bog
       * and almost no height — so this is the whole of the wiring for seeing a
       * battle fought in a country other than the temperate baseline.
       */
      biome: params.get('biome') ?? undefined,
      /**
       * `?town=6,2,0` — rung, fortification, capital — raises the defender's
       * holding on the field.
       *
       * A siege is only ever staged from the campaign, where `battleConfigFor`
       * reads those three numbers off the province being stormed. There was no
       * way at all to *look* at one without playing eight months of a campaign
       * first, which is why the screenshot harness had never photographed a
       * town under assault. Parsed defensively: anything that is not three
       * numbers is no town, which is what every existing link says.
       */
      town: townParam(params.get('town')),
      freeMuster: true,
    });
    adoptWorld(next, MAP_W / 2, MAP_H / 2 + 6, 20);
    menu.hide();
  } else if (params.get('mp') === '1') {
    /**
     * Co-op Hold the Hearth, over the wire.
     *
     * `?mp=1&server=ws://host:8931&room=code[&host=1][&players=2]` — the host
     * may also pass seed/faction/race/kingdom/difficulty; allies inherit the
     * host's recipe through the relay, so one URL differing only in `host=1`
     * is a whole lobby. Everyone builds the same world from the same config
     * and steps it in lockstep from tick 0.
     */
    void startCoop({
      server: relayUrl(),
      room: params.get('room') ?? 'hearth',
      nickname: params.get('name') ?? (params.get('host') === '1' ? 'host' : 'ally'),
      host: params.get('host') === '1',
      players: Number(params.get('players') ?? 2),
      choice: {
        seed,
        factionId: params.get('faction') ?? 'ashen',
        raceId: params.get('race') ?? 'ashen',
        kingdomId: params.get('kingdom') ?? 'ashen_vane',
        difficultyId: params.get('difficulty') ?? 'endless',
        heroId: params.get('hero') ?? '',
      },
    });
    menu.hide();
  }

  /**
   * Bake the strategic chart's ground layers while the boot screen is still up.
   *
   * The relief, the fabric and the shelf are each computed once and cached for
   * the session, and each is a walk over the raster — the relief is the dear
   * one at roughly a second, being 663,000 pixels of two-octave noise. Left to
   * happen lazily they land on the first `paint` of the realm, which is a
   * one-second freeze the first time a player opens the map and never again:
   * the worst possible shape for a stall, because it is unexplained and it
   * happens exactly once so nobody can reproduce it on demand.
   *
   * Here it lands under a screen that already says the game is getting ready,
   * beside the texture bake that costs considerably more. Wrapped, because a
   * chart without shading is the chart that shipped last week and a boot that
   * throws is not.
   */
  status.textContent = 'drawing the ground…';
  try {
    reliefImage();
    textureImage();
    seaImage();
  } catch {
    // Nothing to do about it and nothing that needs saying: the layers each
    // return '' on failure and the chart simply draws without them.
  }

  (document.getElementById('boot') as HTMLElement).classList.add('gone');
  setTimeout(() => (document.getElementById('boot') as HTMLElement).remove(), 600);

  /**
   * On a hearth that asks for accounts, the question is asked on the way in.
   *
   * The doors in front of the play buttons are the rule; this is the manners.
   * Waiting until somebody presses New Game means they meet the question having
   * already decided what they wanted to do, and a card that interrupts an
   * intention reads as an obstacle — whereas the same card at the door reads as
   * a door. It also means the title screen is honest: a player who is going to
   * have to sign in finds out before they have picked a kingdom.
   *
   * Dismissible, and dismissing it changes nothing: the buttons still ask. This
   * is where the question is *first* put, not the only place it is put.
   *
   * After the boot screen has gone, so the card is never behind it.
   */
  running = true;
  if (signIn.configured && !mode) {
    setTimeout(() => void requireAccount(), 650);
  }

  app.ticker.add(() => {
    const dt = Math.min(app.ticker.deltaMS / 1000, 0.25);
    fps = fps * 0.9 + (1 / Math.max(0.0001, dt)) * 0.1;

    /**
     * Keep the camera's idea of the viewport equal to the canvas.
     *
     * The bug this closes was reported as *"a wierd rendering bug in the city
     * where the camera where it actually renders is to the top left of
     * centre"*, and the arithmetic says exactly that: a world point is drawn at
     * `viewW / 2 - x * zoom`, so a `viewW` smaller than the canvas puts the
     * whole world up and to the left of where it belongs, by half the
     * difference. The Hearth is no longer under the middle of the screen and
     * every click lands somewhere other than where it was aimed.
     *
     * The camera was told the size **twice**: once at boot, and once per window
     * `resize` event. Pixi is configured `resizeTo: window`, so the canvas also
     * changes size on its own — when the boot overlay goes, when the layout
     * settles, when the device pixel ratio changes because the window moved to
     * another monitor — and none of those raise a window resize. Whatever the
     * canvas did in between, the camera never heard about it.
     *
     * Two number comparisons a frame, and it cannot drift again for any reason,
     * including reasons nobody has thought of. The `resize` listener stays: it
     * is still the thing that reacts in the same frame as the change rather
     * than the frame after.
     */
    if (scene.camera.viewW !== app.screen.width || scene.camera.viewH !== app.screen.height) {
      scene.resize(app.screen.width, app.screen.height);
    }

    // Who you are belongs to the title screen; in a run the room panel speaks.
    accountBar.setVisible(menu.isOpen);
    if (menu.isOpen) {
      scene.render(world, 0, dt, { companies: [], hoverCompany: -1 });
      return;
    }

    handleCameraKeys(dt);

    // During deployment the simulation still runs — every company is on Hold and
    // the armies are far apart, so nothing happens except formations settling
    // into the shapes you drag them into. That live preview is worth more than a
    // hard pause.

    // Hitstop and pause both work the same way: stop feeding the accumulator
    // and keep drawing. Nothing desynchronises because nothing half-steps.
    if (scene.fx.hitstop <= 0 && !paused && !net?.paused) {
      acc += dt * timescale * scene.fx.slowmo;
      let steps = 0;
      audio.beginFrame();
      // Twelve rather than six: ×8 on a 30 fps machine needs eight ticks a frame
      // to keep up, and a speed control the loop silently refuses to honour is
      // worse than no speed control.
      while (acc >= TICK_DT && steps < 12) {
        scene.capturePrev(world);
        const frameCmds = drain();
        if (net) {
          /**
           * Three destinations, and which one an order takes is the whole of
           * the network model.
           *
           * The **realm** is the server's. Orders for it are sent and nothing
           * more: they are not applied here, not even optimistically, because
           * the board on screen is the board as it actually is rather than a
           * guess that would have to be taken back. They come home in the next
           * update, about a tenth of a second later.
           *
           * A **field where two crowns meet** is stepped on both machines, so
           * its orders are sent *and* applied — the server passes them along
           * and simulates nothing.
           *
           * A **settlement** is nobody else's business. It is simulated on
           * exactly one machine and reaches the realm only as orders: the hosts
           * it raises, the standing it reports.
           */
          const forRealm: Command[] = [];
          const forField: Command[] = [];
          const forCity: Command[] = [];
          const onField = world.battle !== undefined;
          for (const c of frameCmds) {
            (world.campaign ? forRealm : onField ? forField : forCity).push(c);
          }
          if (forRealm.length) net.order(forRealm);
          if (forField.length && world.battle?.pvp === true) net.orderField(forField);
          // …and whatever the other crown ordered on this field since last tick.
          tickGame({ realm: [], city: forCity, battle: [...forField, ...drainField()] });
        } else {
          tickGame(frameCmds);
        }
        scene.consumeEvents(world);
        audio.consume(world.events, audioGain, audioPan, (t) => unitType(t).voice);
        acc -= TICK_DT;
        steps++;
      }
    }

    // Tension drives the ambient bed: quiet during a build phase, a low pressing
    // drone while a wave is on the field.
    const siegeOn = world.battle ? !world.battle.deploying : world.phase === Phase.Siege;
    const pressure = siegeOn ? 0.35 + Math.min(0.65, world.counts.enemy / 120) : 0;
    audio.setTension(pressure);
    maybeAutosave();
    // A room that has stopped talking looks exactly like a quiet realm, so
    // somebody has to be watching the clock.
    net?.heartbeat();
    // A crow, now and then, when nothing is happening.
    ambientTimer -= dt;
    if (ambientTimer <= 0) {
      ambientTimer = 18 + Math.random() * 26;
      if (!siegeOn) audio.ambientAccent(Math.random() < 0.75 ? 'crow' : 'bell');
    }

    /**
     * The Chronicle.
     *
     * Driven entirely off `world.pendingEvent` rather than off a "we showed it"
     * flag, which is what makes loading a save taken with the modal open put
     * the modal straight back up: the pending event is in the save, so the very
     * next frame after `adoptWorld` sees it and reopens. It is also why Escape
     * cannot get rid of it — the `!hud.modalOpen` clause puts it back.
     */
    const evKey = world.pendingEvent ? `${world.pendingEvent.id}#${world.pendingEvent.wave}` : '';
    if (evKey && (shownEventKey !== evKey || !hud.modalOpen)) {
      shownEventKey = evKey;
      hud.showChronicle(world);
    } else if (!evKey && shownEventKey) {
      shownEventKey = '';
      hud.hideModal();
    }

    // Spoils: offer the relic pick the moment the sim produces one.
    if (world.relicOffer.length > 0 && lastRelicOffer !== world.wave) {
      lastRelicOffer = world.wave;
      hud.showRelicOffer(world);
    }
    /**
     * Riders, and the brake.
     *
     * The realm now moves whether or not anybody is looking at it, so the one
     * warning that must not be missed — a host on the road to the city the
     * player has spent the evening building — takes the speed control back off
     * them. Polled off `campaign.warnings`, which is hashed state, so this
     * cannot fire twice for the same host and cannot miss one that appeared
     * while the player was mid-drag.
     *
     * Gated on the turn counter, and that is not a micro-optimisation. The
     * warning list is rebuilt in exactly one place — the end of a council
     * sitting, in `refreshWarnings` — so between two sittings there is nothing
     * whatsoever to find, and scanning for it sixty times a second is sixty
     * times a second of allocation for a list that provably did not move. It
     * matters because the list is not bounded by anything this file controls:
     * on a large board it is every hostile host near the player, and this is a
     * per-frame path on the *settlement* screen as much as on the map.
     */
    const rw = realmOf();
    const rc = rw?.campaign;
    if (rc && rc.turn !== lastWarnTurn) {
      lastWarnTurn = rc.turn;
      // A plain fold rather than filter/map/sort/join: same key, no garbage.
      // Warnings are already sorted by `refreshWarnings`, so order is stable.
      let inbound = '';
      for (const wn of rc.warnings) {
        if (wn.kind !== WarnKind.March || wn.to !== rc.capital) continue;
        inbound += `${wn.armyId},`;
      }
      if (inbound && inbound !== capitalWarnKey) {
        if (timescale > 1) setSpeed(1);
        hud.hint('Riders. A host has taken the road to your gates.', 5);
      }
      capitalWarnKey = inbound;
    }

    // The world map, if we are on it. It redraws only when the board moves.
    if (worldmap.isOpen) worldmap.update(world);
    // The Levies, over it. Same rule and its own signature: it redraws when the
    // levy actually moves, which is once a month on a quiet realm.
    levies.update(realmOf());
    /**
     * The realm's chronicle, wherever the player is standing. On the map it is
     * the modal; in the city it is a banner, because the realm being frozen on
     * a question must not stop the town — see the header of `realmevent.ts`.
     * A battlefield shows neither: `realmOf()` still finds the realm, but a
     * player mid-battle cannot leave anyway, so only the map/city split matters.
     */
    realmEvent.update(world.battle ? null : realmOf(), worldmap.isOpen);
    /**
     * The war menu, and it is the same panel on both screens.
     *
     * Null while the player is already in the fight — `c.pending` is not
     * cleared until `applyBattleResult` lands, so a field battle and a home
     * assault both run with the question still on the table, and a modal put
     * back up over the battlefield would be a panel asking whether to fight the
     * battle being fought behind it.
     */
    warCall.update(world.battle || homeAssault ? null : realmOf());
    // The door back to the realm, and the news of what is marching at you.
    if (travel.isOpen) travel.update(world, campaignWorld);

    /**
     * A host inside your own walls.
     *
     * Polled rather than pushed, for the same reason the Chronicle is: the
     * verdict is a function of the world's state, so a frame that arrives late,
     * early or twice cannot miss it or fire it twice.
     */
    if (homeAssault && !assaultResultShown) {
      const res = homeAssaultResult(world, homeAssault);
      if (res.decided) {
        assaultResultShown = true;
        finishHomeAssault(homeAssault, res);
      }
    }

    // Battle result.
    if (world.battle && world.battle.outcome !== 0 && !battleOutcomeShown) {
      battleOutcomeShown = true;
      showBattleResult();
    } else if (!world.battle && !homeAssault && world.gameOver && !hud.modalOpen && !menu.isOpen) {
      // A capital that falls is the campaign ending, and `finishHomeAssault`
      // has its own sheet for it — the generic death screen would talk over
      // it. The menu guard is for the death sheet's own "Bloodline" button:
      // the world behind the menu is still dead, and without it the sheet
      // re-opens over the spend screen on the next frame.
      showDeathScreen();
    }

    const alpha = clamp(acc / TICK_DT, 0, 1);
    /**
     * Where the ring does not close is *building* information, so it is on while
     * a wall tool is up and through the whole build phase, and off once the
     * horns have gone — a siege has enough on the screen, and by then the answer
     * is no longer actionable. Breaches are drawn either way; those are not
     * advice, they are events.
     */
    scene.showCurtain =
      buildSel.kind === 'wall' ||
      buildSel.kind === 'gate' ||
      buildSel.kind === 'demolish' ||
      (!world.battle && !world.campaign && world.phase === Phase.Build);
    scene.render(
      world,
      alpha,
      dt,
      { companies: selected, hoverCompany: -1, building: selectedBuilding },
      buildPreview(),
    );
    hud.setChrome({
      // In a room the pause is the server's, so the button has to read *its*
      // answer or it would offer to pause a realm that is already stopped.
      paused: paused || net?.paused === true,
      timescale,
      muted: audio.settings.muted,
      hasRealm: campaignWorld !== null,
      onRealm: worldmap.isOpen,
    });
    /**
     * The other world, for the strip. See `Hud.setWorlds`: the crown's purse and
     * the council clock are the realm's, and the alerts, the tax band and the
     * Age are the town's, and exactly one of the two is `world` on any frame.
     */
    hud.setWorlds(cityOf(), realmOf());
    hud.update(world, dt, fps, showFps);
    // The HUD drops a slot that has stopped existing; keep the two in step so
    // the ring on the map cannot outlive the panel describing it.
    selectedBuilding = hud.selectedBuilding;
    tutorial.update(world);
    // The Ledger's first argument is the *town*, and on the world map `world`
    // is the realm — which is a distinction that could not show while the map
    // covered the Ledger, and shows the moment it stops.
    manage.update(cityOf() ?? world, realmOf(), dt);
    hud.hint(hintText());
  });

  function drain(): Command[] {
    return queue.drain();
  }

  /**
   * The realm world, whichever screen we are on. Null outside a campaign.
   *
   * `world` is whatever is being drawn: the city, the map, or a battlefield.
   * `campaignWorld` is the realm when it is parked. One of the two is it.
   */
  function realmOf(): World | null {
    return world.campaign ? world : campaignWorld;
  }

  /**
   * The city world, whichever screen we are on.
   *
   * On the map and on a battlefield the city is parked in `homeWorld`. In the
   * city it *is* `world` — and outside a campaign entirely, `world` is a
   * standalone settlement, which is still a city for stepping purposes. A
   * battle opened from the menu has no city at all.
   */
  function cityOf(): World | null {
    if (world.campaign) return homeWorld;
    if (world.battle) return homeWorld;
    return world;
  }

  /**
   * One tick of everything that is running.
   *
   * The realm and the city both live now, so both are stepped every tick, in a
   * fixed order that `stepGame` owns. Commands go to whichever of them is on
   * screen; a battlefield is neither, and is stepped on its own afterwards.
   *
   * The holds are computed here rather than pushed from the code that opens a
   * battle, for the same reason the Chronicle modal is polled rather than
   * pushed: a state that is recomputed from what is true cannot be left stale by
   * a path somebody forgot about.
   *
   *   - A **field battle** holds both parked worlds. The player is away; the
   *     masons stop and so do the hosts.
   *   - An **assault on the capital** holds the realm and *not* the city. The
   *     city is the battlefield, and a settlement has always kept building
   *     through a siege — that is the whole cost of a siege. The realm freezes
   *     because a fight for your own walls must not also cost you three
   *     provinces you were not there to watch.
   */
  function tickGame(cmds: Command[] | TickCommands): void {
    // Solo, every order belongs to the world on screen. In a room they arrive
    // already sorted, because the sender's screen is the only one that knows.
    const netCmds = Array.isArray(cmds) ? null : cmds;
    const localCmds = Array.isArray(cmds) ? cmds : [];
    const realm = realmOf();
    const city = cityOf();
    const inFieldBattle = world.battle !== undefined;

    const holds: Command[] = [];
    /**
     * The realm does not stop for one player's battle, and in a room it cannot.
     *
     * A hold is *state*, and the realm's state is the server's. Sending this
     * would be one client asking the server to freeze a board three other people
     * are standing on because they personally walked into a fight; not sending
     * it and applying it locally would be a client editing a world it does not
     * own. So in a room the realm keeps its own time, which is the honest cost
     * of playing a live map with other people on it. The city still holds — that
     * one is genuinely this machine's.
     */
    const realmHold = !net && (inFieldBattle || homeAssault !== null);
    const cityHold = inFieldBattle;
    if (realm && realm.battleHold !== realmHold) holds.push(cmd(Cmd.SetBattleHold, realmHold ? 1 : 0));
    const cityHolds: Command[] = [];
    if (city && city.battleHold !== cityHold) cityHolds.push(cmd(Cmd.SetBattleHold, cityHold ? 1 : 0));

    // Diplomacy raised from the Ledger is the realm's business wherever the
    // player happened to be standing when they raised it.
    const forRealm = [
      ...holds,
      ...realmQueue.drain(),
      ...(netCmds ? netCmds.realm : realm === world ? localCmds : []),
    ];
    const forCity = [...cityHolds, ...(netCmds ? netCmds.city : city === world ? localCmds : [])];
    /**
     * In a room the realm is not stepped here, and that is the change.
     *
     * It is simulated in one place — the server — and arrives as state ten times
     * a second. So an order meant for it is *posted* rather than applied:
     * stepping the realm here as well would be this machine writing to a board
     * it is only watching, and the two would part company inside a minute.
     */
    /**
     * And every other town this crown holds, which is the change.
     *
     * They take no commands — there is no way to order a world you are not
     * standing in — but they *run*, because a settlement that stopped when the
     * player looked away would make owning a second town a way of pausing it.
     * `townsInOrder` sorts by province, so the order is a fact about the board
     * rather than about the order somebody clicked things; `stepGame` skips
     * whichever of them is already `city`.
     *
     * Deliberately **not** held for a battle. The two `battleHold` writes above
     * are aimed at the realm and at the world on screen, which is what the hold
     * has always meant: the player is away, and *this* town is the one they
     * were building in. A garrison in a town three provinces from the fight
     * carries on being a garrison.
     */
    const others = towns.size > 1 ? townsInOrder() : EMPTY_TOWNS;
    if (net) {
      if (forRealm.length) net.order(forRealm);
      stepGame(null, city, { city: forCity }, others);
    } else {
      stepGame(realm, city, { realm: forRealm, city: forCity }, others);
    }
    // A battlefield is its own world and belongs to neither slot. In a room
    // its orders arrive already gathered from both crowns.
    if (world !== realm && world !== city) {
      stepWorld(world, netCmds ? netCmds.battle : localCmds);
    }
  }

  /**
   * Open or join a co-op room, and stand the settlement up inside it.
   *
   * One path for the lobby and the `?mp=1` link, and there is now only one path
   * *into a room* as well. Opening a room, joining one that has not started and
   * walking into one that has been running for an hour were three cases under
   * lockstep — build from the recipe, load the stored save, or take a snapshot
   * and replay the rounds since — and they are one case now: the server holds
   * the realm and hands it over. A room is something you come back to tomorrow
   * because the room is where it lives, not because four machines agreed to
   * rebuild it from the same file.
   */
  async function startCoop(o: {
    server: string;
    room: string;
    roomName?: string;
    nickname: string;
    host: boolean;
    players: number;
    /** Reopening a room that has a save behind it rather than starting one. */
    resume?: boolean;
    choice?: CampaignChoice;
  }): Promise<void> {
    /**
     * The gate, and it is here rather than on a button.
     *
     * A hearth that asks for accounts asks for one *now*, before a socket is
     * opened: signing in ends this page and resumes on the way back, and there
     * is no sense in a half-built session waiting through a redirect. A hearth
     * that does not ask — a machine among friends, a development laptop — falls
     * straight through, and the nickname is whatever was typed, exactly as
     * before accounts existed.
     */
    let nickname = o.nickname;
    if (signIn.configured) {
      const handle = await ensureAccount(signIn, accounts);
      // Backed out, or gone to the provider's page and not yet back.
      if (!handle) {
        menu.show();
        return;
      }
      nickname = handle;
      // The room directory is the account's, so opening a room is recorded
      // against the person who opened it and outlives the process playing it.
      const code = o.room;
      try {
        if (o.host) await accounts.openRoom(code, o.roomName ?? code, o.players);
        await accounts.takeSeat(
          code,
          o.choice?.factionId ?? 'ashen',
          o.choice?.raceId ?? 'ashen',
          o.choice?.kingdomId ?? 'ashen_vane',
        );
      } catch (e) {
        // The directory refusing is a real refusal — the room is somebody
        // else's, the crown is taken — and there is no point opening a socket
        // that is about to be told the same thing.
        console.warn('[coop]', (e as Error).message);
        hud.hint(`The room would not have you: ${(e as Error).message}`, 5);
        menu.show();
        return;
      }
    }

    const session = new Session();
    try {
      await session.connect(o.server, o.room, o.host, o.players, nickname, o.roomName, {
        token: await signIn.token(),
      });
    } catch (e) {
      console.warn('[coop]', (e as Error).message);
      menu.show();
      return;
    }
    /**
     * Every seat claims its crown — unless the room has already given it one.
     *
     * A reopened room seats a returning player in the kingdom they played here,
     * off the `pid` in the hello, before they ask for anything. Claiming over
     * the top of that would undo the whole of it: the kingdom screen was visited
     * on the way in, its answer is whatever was highlighted, and sending it
     * would move the player to a nation whose capital is not theirs and hand
     * their own city to nobody.
     */
    if (!session.restored) {
      session.claimCrown({
        factionId: o.choice?.factionId ?? 'ashen',
        raceId: o.choice?.raceId ?? 'ashen',
        kingdomId: o.choice?.kingdomId ?? 'ashen_vane',
        heroId: o.choice?.heroId,
      });
    }
    if (o.host) {
      const c = o.choice;
      session.shareConfig({
        seed: c?.seed ?? (Date.now() & 0x7fffffff),
        factionId: c?.factionId ?? 'ashen',
        raceId: c?.raceId ?? 'ashen',
        kingdomId: c?.kingdomId ?? 'ashen_vane',
        difficultyId: c?.difficultyId ?? 'endless',
        heroId: c?.heroId,
      });
    }
    /**
     * Orders from the other crown on a field the two of them are standing on.
     *
     * Queued rather than applied, because a command must land inside the tick
     * loop like every other: applying one from a socket callback would put a
     * write in the middle of a frame that is halfway through drawing.
     */
    session.onFieldOrders = (cmds) => {
      for (const c of cmds) fieldQueue.push(c);
    };
    /**
     * The room ended, or this client fell out of it.
     *
     * The host leaving is no longer one of the ways this happens — the realm is
     * the server's, so a room outlives whoever opened it. What is left is the
     * connection dropping, and a room the server let go.
     */
    session.onClosed = (why) => {
      hud.showSheet(
        `<h1>The Room Closed</h1><div class="sub">${esc(why)}.</div>` +
          `<p>The run is saved where it was being played. Open the room again and it picks up where it stopped.</p>` +
          `<div class="pausebtns"><span class="seal" data-pause="quit">Back to the menu</span></div>`,
        'closed',
      );
      for (const el of document.querySelectorAll('[data-pause="quit"]')) {
        (el as HTMLElement).onclick = () => {
          hud.hideModal();
          quitToTitle();
        };
      }
    };
    session.onPause = (on, who) => {
      hud.hint(on ? `${who || 'The host'} paused the room` : 'Playing', on ? 0 : 1.5);
    };
    // Things the room should know that are nobody's fault: a seat asked for a
    // pause it may not have, the host filed the room, a crown was freed.
    session.onNotice = (why) => hud.hint(why, 3.5);
    // And things this seat asked for and was refused. Both are the server's own
    // words: there is one statement of what a seat may do and it is not here.
    session.onRefusal = (why) => hud.hint(why, 4);

    /**
     * The board, as it stands. One message, whether this room is a minute old
     * or was opened last week and has been running since the horns went.
     *
     * Or a refusal — the crown is taken, the room is full, the line dropped.
     * Those arrive *instead of* a board, so they come back here as a throw and
     * the player is put back in front of the menu rather than left looking at a
     * settlement that is never going to appear.
     */
    /**
     * The wait itself, on screen.
     *
     * Shown before the board is awaited rather than after, because the whole
     * point of it is the gap between the two. Leaving from here is a real exit —
     * the socket is closed, the seat is given up, and the waiter is rejected so
     * this function does not go on to build a world for a room nobody is in.
     */
    let left = false;
    lobby.show(session, () => {
      left = true;
      session.leave();
      menu.show();
    });
    let begun;
    try {
      begun = await session.waitBegin();
    } catch (e) {
      lobby.hide();
      if (left) return;
      console.warn('[coop]', (e as Error).message);
      session.leave();
      hud.hint(`The room would not take you: ${(e as Error).message}`, 5);
      menu.show();
      return;
    }
    lobby.hide();
    if (left) return;
    const cfg = begun.cfg;
    tutorial.stop();

    /**
     * The realm is *received*, and the shell it is poured into is built here.
     *
     * The map is a pure function of the seed, so both ends generate the same one
     * rather than putting a hundred and forty thousand tiles on the wire — it
     * would be the largest thing crossing it by an order of magnitude, and it is
     * the one part of a realm that never changes. What cannot be derived is what
     * has *happened*, and that is exactly what `adoptRealm` carries.
     *
     * No bloodline on this world, and that is not an omission. Heirlooms are a
     * family's private unlock; folding one player's into a board everybody
     * shares would hand them a realm the others never agreed to — and under
     * lockstep, where each client built its own copy with its own unlocks, it
     * quietly made four subtly different realms out of one recipe.
     */
    const realm = createCampaignWorld({
      seed: cfg.seed,
      factionId: cfg.factionId,
      difficultyId: cfg.difficultyId,
      sandbox: false,
      raceId: cfg.raceId,
      leaderId: cfg.kingdomId,
    });
    adoptRealm(realm, begun.realm);
    /**
     * Which of these crowns am I.
     *
     * Local, and deliberately not taken off the wire: the server plays nobody,
     * and this is the one field about a shared board that is different on every
     * machine. It is set once here and never again — the updates that follow
     * carry only what the server changed, and the server never changes this.
     */
    const cam = realm.campaign;
    if (cam && begun.nation >= 0) {
      cam.playerNation = begun.nation;
      cam.capital = begun.capital;
    }

    /**
     * The capital, by contrast, is private and stays that way.
     *
     * A settlement is simulated by the player standing in it and reaches the
     * shared realm only as orders — the hosts it raises, the standing it reports
     * — which is what keeps four cities from having to run on four machines, and
     * what lets a player keep building while the map is busy elsewhere.
     */
    /**
     * Which kingdom this capital is, and the seating outranks the screen.
     *
     * A returning player passed through the kingdom picker on the way in and its
     * answer is meaningless to them — the room gave them back the crown they
     * wore here before. Building their city from what happened to be highlighted
     * would raise an Ashen capital for an Ironfang nation on the one path where
     * `begun.city` is missing, which is exactly the path a rehosted save takes
     * the first time somebody's capital was never filed.
     */
    const seated = begun.seating.find((c) => c.id === session.playerId);
    const mine = seated
      ? {
          factionId: seated.factionId,
          raceId: seated.raceId,
          kingdomId: seated.kingdomId,
          heroId: seated.heroId,
          difficultyId: cfg.difficultyId,
          seed: cfg.seed,
        }
      : (o.choice ?? {
          factionId: cfg.factionId,
          raceId: cfg.raceId,
          kingdomId: cfg.kingdomId,
          heroId: cfg.heroId,
          difficultyId: cfg.difficultyId,
          seed: cfg.seed,
        });
    let city: World | null = null;
    if (begun.city) {
      try {
        city = loadWorld(begun.city);
      } catch {
        console.warn('[coop] that room’s capital could not be read; raising a new one');
        city = null;
      }
    }
    if (!city) {
      city = createWorld({
        seed: cfg.seed,
        factionId: mine.factionId,
        difficultyId: cfg.difficultyId,
        sandbox: false,
        raceId: mine.raceId,
        leaderId: mine.kingdomId,
        bloodline: ownedBloodline(),
        homeCity: true,
        biome: provinceDef(realm.campaign?.capital ?? 0).terrain,
      });
      initRun(city);
      if (mine.heroId) queue.push(cmd(Cmd.PickHero, 0, 0, 0, 0, mine.heroId));
    }
    campaignWorld = realm;
    homeWorld = city;
    net = session;
    /**
     * The board moves.
     *
     * Ten times a second, carrying only what changed since the last one — which
     * is the whole of the realm's simulation as far as this machine is
     * concerned: no local step, no prediction, nothing to reconcile. Said here
     * rather than when the socket opened because an update is a patch against
     * the one before it, so the first one may only be played once there is a
     * board to play it on; anything that arrived while this world was being
     * built has been kept and comes through now, in order.
     */
    session.watchRealm((u) => applyRealmUpdate(realm, u));
    setSpeed(1);
    adoptWorld(city, city.map.hearthX, city.map.hearthY, 34);
    travel.show();
    menu.hide();
  }

  function handleCameraKeys(dt: number): void {
    if (console_.open) return;
    const speed = (keys.has('shift') ? 46 : 22) / scene.camera.zoom * 30;
    let dx = 0;
    let dy = 0;
    if (keys.has('a') || keys.has('arrowleft')) dx -= 1;
    if (keys.has('d') || keys.has('arrowright')) dx += 1;
    if (keys.has('w') || keys.has('arrowup')) dy -= 1;
    if (keys.has('s') || keys.has('arrowdown')) dy += 1;

    // Edge pan — only once the pointer has actually been over the canvas.
    if (mouseInside) {
      const edge = 12;
      if (mouseX < edge) dx -= 1;
      if (mouseX > window.innerWidth - edge) dx += 1;
      if (mouseY < edge) dy -= 1;
      if (mouseY > window.innerHeight - edge) dy += 1;
    }

    if (dx !== 0 || dy !== 0) {
      const l = Math.hypot(dx, dy) || 1;
      scene.camera.pan((dx / l) * speed * dt, (dy / l) * speed * dt);
    }
  }

  function buildPreview(): BuildPreview | undefined {
    const wx = scene.camera.screenToWorldX(mouseX);
    const wy = scene.camera.screenToWorldY(mouseY);

    if (hud.armedHero) {
      const def = power(hud.armedHero);
      const r = resolvePower(world, def);
      return { kind: 'power', x: wx, y: wy, radius: r.radius, friendlyFire: def.friendlyFire };
    }
    if (hud.armed) {
      const def = power(hud.armed);
      const r = resolvePower(world, def);
      return { kind: 'power', x: wx, y: wy, radius: r.radius, friendlyFire: def.friendlyFire };
    }
    if (wallDragStart) {
      // A wall drag previews the *thickened* walk, because that is what will be
      // built and what it will cost. Roads keep the thin one.
      const thick = buildSel.kind === 'wall' || buildSel.kind === 'gate';
      const line = thick ? wallLineTiles : lineTiles;
      return {
        kind: 'line',
        tiles: line(wallDragStart.x, wallDragStart.y, Math.floor(wx), Math.floor(wy)),
      };
    }
    if (buildSel.kind === 'building') {
      const tx = Math.floor(wx);
      const ty = Math.floor(wy);
      return {
        kind: 'building',
        typeIdx: buildSel.typeIdx,
        x: tx,
        y: ty,
        valid: canPlaceBuilding(world, buildSel.typeIdx, tx, ty) === '',
      };
    }
    if (
        buildSel.kind === 'wall' ||
        buildSel.kind === 'gate' ||
        buildSel.kind === 'road' ||
        buildSel.kind === 'bridge'
      ) {
      if (buildSel.kind === 'wall' && keys.has('control')) {
        const half = (TOWER_SIZE / 2) | 0;
        const tx = Math.floor(wx) - half;
        const ty = Math.floor(wy) - half;
        return { kind: 'tower', x: tx, y: ty, valid: canPlaceTower(world, tx, ty) === '' };
      }
      return { kind: 'line', tiles: [[Math.floor(wx), Math.floor(wy)]] };
    }
    if (rightDrag && selected.length > 0) {
      return { kind: 'drag', x: rightDrag.wx, y: rightDrag.wy, x2: wx, y2: wy };
    }
    return undefined;
  }

  /**
   * The curtain tile under a world point, or −1. Forgiving by three tiles,
   * because a player pointing at their own wall from a tactical camera is
   * pointing at roughly the right stone and `Cmd.Garrison` is forgiving in
   * exactly the same way. Kept in step by using the same helper.
   */
  function wallUnder(wx: number, wy: number): number {
    const tx = Math.floor(wx);
    const ty = Math.floor(wy);
    if (tx < 0 || ty < 0 || tx >= MAP_W || ty >= MAP_H) return -1;
    if (isCurtain(world.map, world.map.idx(tx, ty))) return world.map.idx(tx, ty);
    return nearestCurtain(world.map, tx, ty, 3);
  }

  /**
   * A tower is placed by its centre and stamped from its top-left, so the block
   * lands where the cursor is rather than down and to the right of it.
   */
  function towerCommandAt(wx: number, wy: number): Command {
    const half = (TOWER_SIZE / 2) | 0;
    return cmd(Cmd.WallTower, Math.floor(wx) - half, Math.floor(wy) - half);
  }

  function hintText(): string {
    if (paused) return 'Paused — P to continue';
    if (timescale !== 1) return `Time ×${timescale} — − and + to change`;
    if (hud.armedHero) {
      const def = power(hud.armedHero);
      const i = heroUnit(world);
      const wx = scene.camera.screenToWorldX(mouseX);
      const wy = scene.camera.screenToWorldY(mouseY);
      const far = i >= 0 && Math.hypot(wx - world.units.x[i], wy - world.units.y[i]) > HERO_ABILITY_RANGE;
      return far
        ? `${def.name} — too far from him. He has to be able to reach it.`
        : `${def.name} — ${world.hero.name} casts it${def.friendlyFire ? '. It will hit your own.' : ''}`;
    }
    if (hud.armed) {
      const def = power(hud.armed);
      return `${def.name} — click to place${def.friendlyFire ? '. It will hit your own.' : ''}`;
    }
    if (buildSel.kind === 'building') {
      const def = buildingType(buildSel.typeIdx);
      const wx = Math.floor(scene.camera.screenToWorldX(mouseX));
      const wy = Math.floor(scene.camera.screenToWorldY(mouseY));
      const err = canPlaceBuilding(world, buildSel.typeIdx, wx, wy);
      return err ? `${def.name} — ${err}` : `${def.name} — click to place, shift to keep placing`;
    }
    if (buildSel.kind === 'wall') {
      if (keys.has('control')) {
        const half = (TOWER_SIZE / 2) | 0;
        const tx = Math.floor(scene.camera.screenToWorldX(mouseX)) - half;
        const ty = Math.floor(scene.camera.screenToWorldY(mouseY)) - half;
        const err = canPlaceTower(world, tx, ty);
        return err ? `Tower — ${err}` : 'Tower — click to raise it. Eight will stand on it.';
      }
      return 'Wall — drag a line · Ctrl+click to raise a tower';
    }
    if (buildSel.kind === 'gate') return 'Gate — drag a line';
    if (buildSel.kind === 'road') return 'Road — drag a line';
    if (buildSel.kind === 'bridge') return 'Bridge — drag across deep water';
    if (buildSel.kind === 'demolish') return 'Demolish — click a building, wall or tower';
    if (selected.length > 0) {
      /**
       * The hint has to name the gesture the player actually has.
       *
       * This line is permanent while anything is selected, and on a phone it
       * said "right-click … F formation · G stance · H hold" to somebody with
       * no right button and no keyboard — four lines of it, laid across the
       * scouting report. `TouchInput` maps a long press onto the right button
       * precisely so every verb still exists; what it cannot do is rewrite the
       * sentence that tells you so.
       *
       * Asked per frame rather than captured once: a laptop with a touchscreen
       * is a real machine, and which of the two a player is using is a thing
       * that changes mid-session.
       */
      const touch = TouchInput.isTouch;
      const wx = scene.camera.screenToWorldX(mouseX);
      const wy = scene.camera.screenToWorldY(mouseY);
      if (mouseInside && wallUnder(wx, wy) >= 0) {
        return touch
          ? 'Hold on the wall to man it · they hold it until you order them elsewhere'
          : 'Right-click the wall to man it · they hold it until you order them elsewhere';
      }
      return touch
        ? 'Hold to move · the order bar sets formation, stance and hold'
        : 'Right-click to move · right-drag to set the line · F formation · G stance · H hold';
    }
    return '';
  }

  /**
   * Write the whole game, not just the world on screen.
   *
   * A campaign is the realm *and* the capital, and saving only whichever one
   * the player happened to be looking at would silently throw the other away.
   * `world` is whichever is live, so it is folded back into the right slot
   * before writing.
   */
  function currentGameSlots(): {
    city: World | null;
    realm: World | null;
    at: 'realm' | 'city';
    towns: World[];
  } {
    if (!campaignWorld) return { city: world, realm: null, at: 'city', towns: [] };
    const realm = world.campaign ? world : campaignWorld;
    const city = world.campaign ? homeWorld : world;
    /**
     * …and every other town, which is the difference between saving a game and
     * saving a screen.
     *
     * A second town that is not written here is a second town the player loses
     * silently — hours of building, gone with no error and no way to tell it
     * happened until they next open the door and find a hamlet. It is the one
     * failure this whole pass is most likely to have, which is why
     * `tests/towns.test.ts` saves a two-town game, loads it and plays both on.
     * `writeGame` drops whichever of these is `city` rather than writing it
     * twice.
     */
    return { city, realm, at: world.campaign ? 'realm' : 'city', towns: townsInOrder() };
  }

  /**
   * The way out, and the only screen that offers one.
   *
   * A run had no exit but the tab's close button, which is a poor thing to
   * offer somebody an hour into a campaign — and worse when the save they
   * would want is one keypress away and nothing says so. Three doors: back to
   * the game, save where you stand, or save and leave. A battle cannot be
   * saved, so it says that rather than offering a button that would lie.
   */
  function showPauseMenu(): void {
    const canSave = !world.battle && !homeAssault && !world.gameOver;
    hud.showSheet(
      `<h1>Stand Down</h1>` +
        `<div class="sub">${
          canSave
            ? 'The realm keeps turning while this is open. It will wait.'
            : 'A fight is not saved — it is meant to be finished in one sitting.'
        }</div>` +
        `<div class="pausebtns">` +
        `<span class="seal" data-pause="resume">Back to it</span>` +
        (canSave ? `<span class="seal ghost" data-pause="save">Save</span>` : '') +
        `<span class="seal ghost" data-pause="quit">${canSave ? 'Save and leave' : 'Leave'}</span>` +
        `</div>`,
      'pause',
    );
    for (const el of document.querySelectorAll('[data-pause]')) {
      (el as HTMLElement).onclick = () => {
        const what = (el as HTMLElement).dataset.pause;
        if (what === 'resume') {
          hud.hideModal();
          return;
        }
        if (what === 'save') {
          doSave();
          hud.hideModal();
          return;
        }
        // Leaving: save what can be saved, put the run down, and go home.
        if (canSave) doSave();
        hud.hideModal();
        quitToTitle();
      };
    }
  }

  /**
   * Put the run down and show the title.
   *
   * A room is left properly rather than abandoned — the seat is given up, and
   * the realm carries on being played by whoever is still in it.
   */
  function quitToTitle(): void {
    paused = false;
    // The last thing this seat says is where its city got to. The realm needs
    // no such courtesy: it never left the server.
    if (net) mirrorCity(cityOf());
    net?.leave();
    net = null;
    lobby.hide();
    homeAssault = null;
    tutorial.stop();
    worldmap.hide();
    travel.hide();
    manage.hide();
    menu.show();
  }

  function doSave(): void {
    if (world.battle) {
      hud.hint('Battles are not saved — they are meant to be lost in one sitting.', 3);
      return;
    }
    if (homeAssault) {
      hud.hint('Not with them inside the walls. An assault is fought in one sitting.', 3);
      return;
    }
    const { city, realm, at, towns: allTowns } = currentGameSlots();
    /**
     * In a room, this capital goes to the room as well.
     *
     * The realm does not: it is simulated on the server and saved there, by the
     * process that owns it, on its own beat. A client sending its copy back
     * would be a machine that only watches the board telling the board what it
     * is — and under lockstep, where everyone had a copy, that is precisely how
     * one seat's realm could end up being everybody's.
     */
    mirrorCity(city);
    /**
     * And in a room, the host files the room.
     *
     * Two different saves and it is worth keeping them apart. `mirrorCity` is
     * this seat's own capital going to the room under its own crown — every seat
     * does that, there is nothing to be authorised, a seat can only ever write
     * its own. `saveRoom` is the *room*: the realm as the server has it plus
     * everybody's cities, written now instead of on the next thirty-second beat.
     * That one is the host's, refused on the server for anybody else, so it is
     * asked for only by the seat that holds the room.
     */
    if (net?.holdsRoom) net.saveRoom();
    const r = writeGame(city, realm, at, undefined, allTowns);
    const where = realm?.campaign ? `turn ${realm.campaign.turn}` : `wave ${world.wave}`;
    const store = storageStatus();
    hud.hint(
      r.ok
        ? `Saved at ${where}. (${(r.bytes / 1024).toFixed(0)} kB → ${store.location})`
        : `Could not save: ${r.error ?? 'unknown'}`,
      3,
    );
    /**
     * The file backend answers before the disk does — it has to, because this
     * is a keypress and a keypress cannot await three megabytes. So the honest
     * "it worked" comes a moment later, and only ever corrects the hint
     * downwards. Silently swallowing a failed write is exactly the failure mode
     * the fallback directory exists to prevent; it would be absurd to reopen it
     * here.
     */
    if (r.ok) {
      void flushSaves().then((done) => {
        if (!done.ok) hud.hint(`The save did not reach the disk: ${done.error ?? 'unknown'}`, 5);
      });
    }
  }

  function doLoad(): void {
    // The hotkey means "the run I am playing", which is the slot this session
    // has been writing to. Falling back to the newest slot covers the one case
    // that is not that: a fresh session that has not saved anything yet.
    const g = readGame() ?? readGame(newestSlot() ?? 1);
    if (!g) {
      hud.hint('Nothing saved.', 3);
      return;
    }
    restoreTowns(g);
    campaignWorld = g.realm;
    homeWorld = g.realm ? g.city : null;
    if (g.at === 'realm' && g.realm) {
      adoptWorld(g.realm, MAP_W / 2, MAP_H / 2, 20);
      campaignWorld = g.realm;
      travel.hide();
      worldmap.show(g.realm);
      hud.hint(`Loaded — turn ${g.realm.campaign?.turn ?? 0}.`, 3);
      return;
    }
    const city = g.city ?? g.realm;
    // A crown new to a room that already has a realm keeps the realm and
    // raises only its own capital — building a fresh board here would have
    // quietly replaced everyone's war with an empty one.
    if (!city) {
      hud.hint('That save could not be read.', 3);
      return;
    }
    worldmap.hide();
    adoptWorld(city, city.map.hearthX, city.map.hearthY, scene.camera.zoom);
    homeWorld = campaignWorld ? city : null;
    if (campaignWorld) travel.show();
    else travel.hide();
    hud.hint(`Loaded — wave ${city.wave}.`, 3);
  }

  // Autosave at the start of every build phase: the moment a run is worth
  // keeping is the moment you have just survived something.
  //
  // Not at wave 0. A brand-new campaign sits in Build with wave 0 on its very
  // first frame, so autosaving there overwrote the player's existing Continue
  // save with an empty settlement before they had touched anything — clicking
  // "New run" to look at the faction list destroyed the run they had going.
  function maybeAutosave(): void {
    if (world.battle || world.gameOver || homeAssault) return;
    const { city, realm, at, towns: allTowns } = currentGameSlots();
    /**
     * A campaign autosaves on the month, whichever screen the player is on.
     *
     * This used to read `world.campaign` — "am I looking at the map" — which was
     * the same question back when the map only moved while you were looking at
     * it. It is not the same question any more: months now pass while the player
     * is laying out a granary, and keying the autosave to the screen would mean
     * a player who stays in their city never gets one.
     */
    const turn = realm?.campaign?.turn;
    if (turn !== undefined) {
      if (turn === lastAutosaveTurn) return;
      lastAutosaveTurn = turn;
      writeGame(city, realm, at, undefined, allTowns);
      mirrorCity(city);
      return;
    }
    if (world.phase !== Phase.Build) return;
    if (world.wave < 1) return;

    if (world.wave === lastAutosaveWave) return;
    lastAutosaveWave = world.wave;
    writeGame(city, realm, at, undefined, allTowns);
    mirrorCity(city);
  }

  /**
   * Send this crown's capital to the room, on the beat the local autosave uses.
   *
   * The realm needs no help — it is simulated on the server and saved there,
   * every half minute, by the process that owns it. A capital has exactly one
   * copy and it is this one, so a room that only heard about it when somebody
   * remembered to press save would put a returning player back in a city hours
   * behind the war they left it in.
   *
   * Once a month of realm time, which is a few minutes of play, and the socket
   * compresses it. Cheaper than the alternative it replaces: nothing.
   */
  function mirrorCity(city: World | null): void {
    // A dead capital is not a capital to come back to; the room keeps the last
    // one that was standing.
    if (net && city && !city.gameOver) net.pushCity(saveWorld(city, Date.now()));
  }

  function showBattleResult(): void {
    const b = world.battle;
    if (!b) return;
    const won = b.outcome > 0;
    const mine = armyStrength(world, b.player);
    const theirs = armyStrength(world, b.enemy);
    const mins = Math.floor(b.elapsed / 60);
    const secs = Math.floor(b.elapsed % 60);
    hud.showSheet(
      `<h1>${won ? 'The Field Is Yours' : 'Your Army Breaks'}</h1>` +
        `<div class="sub">${esc(b.scenario.flavour)}</div>` +
        `<div class="cr">` +
        `${esc(b.player.name)}: <b>${mine.alive}</b> of ${mine.total} still standing.<br>` +
        `${esc(b.enemy.name)}: <b>${theirs.alive}</b> of ${theirs.total} still standing.<br>` +
        `Decided in <b>${mins}:${secs.toString().padStart(2, '0')}</b>.` +
        `</div>` +
        `<div style="margin-top:18px;display:flex;gap:10px">` +
        (campaignWorld
          ? `<span class="seal" id="torealm">Back to the realm</span>`
          : `<span class="seal" id="again">Again</span>`) +
        `<span class="seal ghost" id="tomenu">The menu</span></div>`,
    );
    // A battle fought off the world map reports back to it rather than restarting.
    const realm = document.getElementById('torealm');
    if (realm) {
      realm.onclick = () => {
        hud.hideModal();
        returnToCampaign();
      };
    }
    const again = document.getElementById('again');
    if (again) {
      again.onclick = () => {
        const cfg = world.config as BattleConfig;
        const next = createBattle({ ...cfg, seed: (Date.now() & 0x7fffffff) });
        adoptWorld(next, MAP_W / 2, MAP_H / 2 + 6, 20);
      };
    }
    const back = document.getElementById('tomenu');
    if (back) {
      back.onclick = () => {
        hud.hideModal();
        worldmap.hide();
        campaignWorld = null;
        menu.show();
      };
    }
  }

  /**
   * Runs whose death has already been paid into the Bloodline. The death
   * screen is re-entered every frame the sheet is closed while the world is
   * still dead, so the credit has to be idempotent per *world*, not per call
   * — and a WeakSet keyed on the world object is exactly that, with nothing
   * to reset when "Again" swaps the world out.
   */
  const bankedRuns = new WeakSet<World>();

  function showDeathScreen(): void {
    const s = world;
    const stats = statsOf(s);

    // The run's worth, paid into the bank exactly once.
    const earned = s.wave > 0 ? legacyEarned(s.wave, s.difficulty.budgetMul) : 0;
    if (earned > 0 && !bankedRuns.has(s)) {
      bankedRuns.add(s);
      bloodline().bankRun(earned, s.wave);
    }
    const held = bloodline().state().legacy;

    // The company the player actually leaned on — most-raised soldier type,
    // villagers excluded, because "favourite company: Villager" is every run.
    let favIdx = -1;
    let favCount = 0;
    for (const k of Object.keys(stats.playerUsedByType)) {
      const idx = Number(k);
      const n = stats.playerUsedByType[idx] ?? 0;
      if (idx !== VILLAGER_TYPE && n > favCount) {
        favIdx = idx;
        favCount = n;
      }
    }
    const favourite = favIdx >= 0 ? `${esc(unitType(favIdx).name)} — ${favCount} raised` : 'none raised';

    const row = (label: string, value: string): string =>
      `<span>${label}</span><b>${value}</b>`;
    const summary =
      `<div class="cr" style="display:grid;grid-template-columns:auto auto;gap:3px 18px;justify-content:center;text-align:left">` +
      row('Wave reached', `${s.wave}`) +
      row('Age', `${s.age}`) +
      row('Kills', `${stats.kills}`) +
      row('Losses', `${stats.losses}`) +
      row('Buildings raised', `${stats.buildingsBuilt}`) +
      row('Buildings lost', `${stats.buildingsLost}`) +
      row('Peak population', `${stats.peakPop}`) +
      row('Powers cast', `${stats.powersCast}`) +
      row('Favourite company', favourite) +
      row('Seed', seedToCode(s.config.seed)) +
      `</div>`;

    const legacyLine =
      earned > 0
        ? `<div class="sub" style="margin-top:14px;color:var(--gold-pale)">The family remembers. ` +
          `<b>+${earned} Legacy</b> banked — ${held} held.</div>`
        : `<div class="sub" style="margin-top:14px">No wave was survived, so nothing is banked. ` +
          `Legacy is paid by depth.</div>`;

    hud.showSheet(
      `<h1>The Hearth Goes Out</h1>` +
        `<div class="sub">${esc(s.causeOfDeath)}</div>` +
        summary +
        legacyLine +
        `<div style="margin-top:18px">` +
        `<span class="seal" id="again">Again</span> ` +
        `<span class="seal" id="to-bloodline">The Bloodline</span>` +
        `</div>`,
    );
    const btn = document.getElementById('again');
    if (btn) {
      btn.onclick = () => {
        // A fresh seed and a fresh read of the bank — anything bought since
        // this run began comes along.
        world = createWorld({
          ...world.config,
          seed: (Date.now() & 0x7fffffff),
          bloodline: ownedBloodline(),
        });
        initRun(world);
        scene.clear();
        scene.camera.centreOn(world.map.hearthX, world.map.hearthY);
        selected = [];
        hud.setSelection(selected);
        hud.hideModal();
        lastRelicOffer = 0;
        shownEventKey = '';
      };
    }
    const toBloodline = document.getElementById('to-bloodline');
    if (toBloodline) {
      toBloodline.onclick = () => {
        hud.hideModal();
        menu.show('bloodline');
      };
    }
  }

  // Expose a little for the console and for debugging in devtools.
  Object.assign(globalThis, {
    hearth: {
      get world() {
        return world;
      },
      /** The determinism fingerprint, for the co-op sync test and the curious. */
      hash: () => hashWorld(world),
      scene,
      queue,
      setTimescale: (t: number) => {
        // The console may ask for anything; the keys walk the ladder. Snapping
        // `speedIdx` to the nearest rung keeps + and − sensible afterwards.
        timescale = clamp(t, 0, 8);
        let best = 0;
        for (let i = 1; i < SPEEDS.length; i++) {
          if (Math.abs(SPEEDS[i] - timescale) < Math.abs(SPEEDS[best] - timescale)) best = i;
        }
        speedIdx = best;
      },
      /**
       * Two doors for the screenshot harness, and nothing it could not do by
       * hand with a mouse.
       *
       * `clickCanvas` synthesises the exact pair of events a real click sends,
       * because selecting a building is a `mousedown`/`mouseup` pair on the
       * canvas and there is no other way for a headless run to reach it. The
       * lookup beside it saves the harness from importing the data layer.
       */
      buildingTypeIndex,
      /**
       * The other two worlds, for the harness.
       *
       * `world` is whichever is being drawn, and a shot of the *city* that
       * wants to stage something on the realm — a battle waiting to be
       * answered, say — had no way to reach it at all. Getters rather than
       * fields, because `adoptWorld` swaps the objects wholesale.
       */
      get realm() {
        return realmOf();
      },
      get city() {
        return cityOf();
      },
      clickCanvas: (x: number, y: number) => {
        const opts = { clientX: x, clientY: y, button: 0, bubbles: true };
        canvas.dispatchEvent(new MouseEvent('mousedown', opts));
        window.dispatchEvent(new MouseEvent('mouseup', opts));
      },
    },
  });
}

// ── Helpers ────────────────────────────────────────────────────────────────

function toggle(list: number[], id: number): number[] {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

/** `team` is the caller's own colours — see `myTeam` in the loop. */
function companyAt(w: World, x: number, y: number, team: number): number {
  let best = -1;
  let bestD = 2.2;
  for (const c of w.companies) {
    if (c.team !== team || c.dead) continue;
    for (const h of c.members) {
      const i = w.units.resolve(h);
      if (i < 0) continue;
      const d = Math.hypot(w.units.x[i] - x, w.units.y[i] - y);
      if (d < bestD) {
        bestD = d;
        best = c.id;
      }
    }
  }
  return best;
}

/** `team` is the caller's own colours — see `myTeam` in the loop. */
function companiesInBox(
  w: World,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  team: number,
): number[] {
  const lo = { x: Math.min(x0, x1), y: Math.min(y0, y1) };
  const hi = { x: Math.max(x0, x1), y: Math.max(y0, y1) };
  const out: number[] = [];
  for (const c of w.companies) {
    if (c.team !== team || c.dead) continue;
    for (const h of c.members) {
      const i = w.units.resolve(h);
      if (i < 0) continue;
      if ((w.units.flags[i] & UF.Villager) !== 0) continue;
      const ux = w.units.x[i];
      const uy = w.units.y[i];
      if (ux >= lo.x && ux <= hi.x && uy >= lo.y && uy <= hi.y) {
        out.push(c.id);
        break;
      }
    }
  }
  return out;
}

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] as string);
}

function nextFrame(): Promise<void> {
  return new Promise((r) => requestAnimationFrame(() => r()));
}

void MAP_W;
void MAP_H;
void Phase;

boot().catch((err) => {
  console.error(err);
  const status = document.getElementById('boot-status');
  if (!status) return;
  /**
   * The error itself, on the screen. 'See the console' was written for the
   * browser, where there is one; in the packaged shell there is no console a
   * player can open, so a boot failure was one unactionable sentence and the
   * only way to learn more was a developer rebuilding the app around the
   * question. The message and the top of the stack cost nothing and turn a
   * report of 'it did not light' into a report with the failure in it.
   */
  const e = err as Error;
  const detail = [e?.message ?? String(err), ...(e?.stack ?? '').split('\n').slice(1, 4)].join('\n');
  status.textContent = `it did not light.\n\n${detail}`;
  (status as HTMLElement).style.whiteSpace = 'pre-wrap';
  (status as HTMLElement).style.maxWidth = '80ch';
});
